import { restoreGoogleSession, loginWithGoogle, clearGoogleSession, createReviewerSession, setGoogleWalletOptions } from "./utils/googleSession";
import { WhatifWalletPlugin, handleWhatifWalletReturn, connectProofOf } from "./utils/whatifWallet";
import { launchCompanionApp } from "./utils/appLauncher";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import SocialLinks from './SocialLinks';
import { SessionKit, Session } from "@wharfkit/session";
import WebRenderer from "@wharfkit/web-renderer";
import { WalletPluginAnchor } from "@wharfkit/wallet-plugin-anchor";
import { WalletPluginCloudWallet } from '@wharfkit/wallet-plugin-cloudwallet';
import { WalletPluginScatter } from "@wharfkit/wallet-plugin-scatter";
import { db } from "./firebase.js";
import { doc, getDoc, setDoc, updateDoc, collection, onSnapshot, query, where, orderBy, limit, getDocs, documentId } from "firebase/firestore";
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from "firebase/auth";
import { auth, functions, appCheck } from './firebase';
import characterImage from './assets/firecat.png';
import placeholderImage from './assets/equip.png';
import backgroundImage from './assets/background.png';
import DailyBoosts from './DailyBoosts';
import { Helmet } from "react-helmet";
import './styles.css';
// --- 1. UPDATE IMPORTS AT THE TOP OF src/App.js ---
// Ensure SUPPORTED_COLLECTIONS is imported from your config file
import {
  ipfsGateway,
  FIREBASE_PROJECT_ID,
  API_BASE_URL,
  BASE_MAX_STAMINA,
  TOKEN_CONTRACT,
  TOKENS,
  tokenContracts,
  tokenDecimals,
  buildingConfigClient,
  ATOMIC_API_ENDPOINTS,
  itemTemplates,
  EMBLEM_NAMES,
  MASTER_ACCOUNT_WAX_ID,
  ADMIN_ACCOUNTS,
  STAKING_TIERS,
  VAULT_TIERS,
  VAULT_TOKEN_INFO,
  VAULT_TOKEN_VALUE,
  getUpgradeCostClient,
  Client,
  calculateDiminishedBoost,
  calculateBaseMP,
  calculateXpForNextLevel,
  SUPPORTED_COLLECTIONS
} from './constants/gameConfig';
import CharacterDisplay from './components/CharacterDisplay';
import BadgeClaim from './components/BadgeClaim';
import NFTStakingPage from './pages/NFTStakingPage';
import PartnerPortal from './pages/PartnerPortal';
import AboutUs from './pages/AboutUs';
import VaultPage from './pages/VaultPage';
import DungeonTimer from './components/DungeonTimer';
import LeaderboardPage from './pages/LeaderboardPage';
import LastDailyLeaderboardPage from './pages/LastDailyLeaderboardPage';
import LastSeasonalLeaderboardPage from './pages/LastSeasonalLeaderboardPage';
import TodaysAirdrops from './pages/TodaysAirdrops';
import AdminPanel from './pages/AdminPanel';
import BlendingPage from './pages/BlendingPage';
import StorePage from './pages/StorePage';
import GangsPage from './pages/GangsPage';
import HiLoJackpotPage from './pages/HiLoJackpotPage';
import SEO from './components/SEO';
import QuartermasterAI from './components/QuartermasterAI';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import UnboxPage from './pages/UnboxPage';
import WheelOfRichesModal from './components/WheelOfRichesModal';
import { ADS_ENABLED, useAdsSwitch } from './config/admob';
import AdPrivacyChoices from './components/AdPrivacyChoices';
import AndroidPromoModal from './components/AndroidPromoModal';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

const isNative = Capacitor.isNativePlatform();

const WebPortalGate = ({ featureName }) => (
  <div style={{
    textAlign: 'center',
    padding: '40px 24px',
    background: 'linear-gradient(145deg, rgba(20,25,35,0.92), rgba(10,12,18,0.96))',
    borderRadius: '16px',
    maxWidth: '650px',
    margin: '40px auto',
    border: '1px solid rgba(0, 229, 255, 0.35)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.8)',
    color: '#e0e0e0'
  }}>
    <div style={{ fontSize: '50px', marginBottom: '16px' }}>🎮</div>
    <h2 style={{ color: '#00e5ff', marginTop: 0, marginBottom: '10px', fontSize: '24px' }}>
      Not in the Android App
    </h2>
    <p style={{ color: '#ff9800', fontWeight: 'bold', fontSize: '16px', margin: '10px 0 16px' }}>
      {featureName || 'This feature'}
    </p>
    <p style={{ color: '#ccc', lineHeight: '1.6', fontSize: '14px', maxWidth: '520px', margin: '0 auto 20px' }}>
      This feature isn't available in the Android app.
    </p>
    <p style={{ color: '#888', fontSize: '13px', margin: '0 auto 25px', maxWidth: '480px' }}>
      Your dungeon crawler quests, battle progression, character gear, daily rewards, and leaderboards continue seamlessly on both mobile and web.
    </p>
  </div>
);

// 1. Auth & Core
const verifyWaxLoginCallable = httpsCallable(functions, 'verifyWaxLogin');

// One-time login challenge. WHATIF Wallet's passkey signs one as it connects, so connecting is the
// sign-in too; Cloud Wallet signs one from the VERIFY IDENTITY click. It's fetched when the player
// presses Connect, so neither has to wait on a slow request (Cloud Wallet's popup is blocked if it
// opens after one). Each nonce is used once.
let loginNoncePromise = null;
let loginNonceFetchedAt = 0;
const prefetchLoginNonce = () => {
  loginNonceFetchedAt = Date.now();
  loginNoncePromise = httpsCallable(functions, 'getLoginNonce')().then((res) => res.data.nonce);
  loginNoncePromise.catch(() => { loginNoncePromise = null; });
};
const takeLoginNonce = () => {
  if (!loginNoncePromise || Date.now() - loginNonceFetchedAt > 8 * 60 * 1000) prefetchLoginNonce();
  const nonce = loginNoncePromise;
  prefetchLoginNonce(); // ready for a retry
  return nonce;
};
const claimDailyLoginRewardCallable = httpsCallable(functions, 'claimDailyLoginReward');
const purchaseMpBoostCallable = httpsCallable(functions, 'purchaseMpBoost');
const syncPlayerVaultStateCallable = httpsCallable(functions, 'syncPlayerVaultState');

const equipItemCallable = httpsCallable(functions, 'equipItem');

// 2. Unboxing & Blending

// 3. Minigames & Dungeon
const claimDailyChestCallable = httpsCallable(functions, 'claimDailyChest');
const startDungeonRunCallable = httpsCallable(functions, 'startDungeonRun');
const claimDungeonRunCallable = httpsCallable(functions, 'claimDungeonRun');
const claimFaucetCallable = httpsCallable(functions, 'claimFaucet');
const playHiLoCallable = httpsCallable(functions, 'playHiLo');
const playCrashCallable = httpsCallable(functions, 'playCrash');
const cashOutCrashCallable = httpsCallable(functions, 'cashOutCrash');
const crashStatusCallable = httpsCallable(functions, 'crashStatus');
const speedUpDungeonRunCallable = httpsCallable(functions, 'speedUpDungeonRun');
const restAtCampfireCallable = httpsCallable(functions, 'restAtCampfire');
const blessNextDungeonRunCallable = httpsCallable(functions, 'blessNextDungeonRun');
const spinWheelOfRichesCallable = httpsCallable(functions, 'spinWheelOfRiches');
const getAndroidPerksStatusCallable = httpsCallable(functions, 'getAndroidPerksStatus');

// 4. Economy & Store
const purchaseStoreItemCallable = httpsCallable(functions, 'purchaseStoreItem');
const requestWithdrawalCallable = httpsCallable(functions, 'requestWithdrawal');
const verifyDepositCallable = httpsCallable(functions, 'verifyDeposit');

// 5. Gangs & Staking
const createGangCallable = httpsCallable(functions, 'createGang');
const joinGangCallable = httpsCallable(functions, 'joinGang');
const leaveGangCallable = httpsCallable(functions, 'leaveGang');
const upgradeGangBuildingCallable = httpsCallable(functions, 'upgradeGangBuilding');
const depositToGangTreasuryCallable = httpsCallable(functions, 'depositToGangTreasury');
const activateGangPassCallable = httpsCallable(functions, 'activateGangPass');
const initiateNftStakeCallable = httpsCallable(functions, 'initiateNftStake');
const verifyNftStakeCallable = httpsCallable(functions, 'verifyNftStake');
const unstakeNftCallable = httpsCallable(functions, 'unstakeNft');

// 6. Partner Portal
const initiatePartnerPoolCallable = httpsCallable(functions, 'initiatePartnerPool');
const verifyPartnerDepositCallable = httpsCallable(functions, 'verifyPartnerDeposit');

// 7. Admin Panel
const adminResetAllDailyPointsCallable = httpsCallable(functions, 'adminResetAllDailyPoints');
const adminResetAllWeeklyPointsCallable = httpsCallable(functions, 'adminResetAllWeeklyPoints');
const adminResetWeeklyPointsCallable = httpsCallable(functions, 'adminResetWeeklyPoints');
const adminRestoreWeeklyPointsCallable = httpsCallable(functions, 'adminRestoreWeeklyPoints');
const adminSendWeeklyPayoutCallable = httpsCallable(functions, 'adminSendWeeklyPayout');
const adminSendWeeklyDiscordAnnouncementCallable = httpsCallable(functions, 'adminSendWeeklyDiscordAnnouncement');
const adminConfigureUnboxCallable = httpsCallable(functions, 'adminConfigureUnbox');
const adminTroubleshootUnboxCallable = httpsCallable(functions, 'adminTroubleshootUnbox');
const adminTransferPointsCallable = httpsCallable(functions, 'adminTransferPoints');
const adminListStoreItemCallable = httpsCallable(functions, 'adminListStoreItem');
const adminDelistStoreItemCallable = httpsCallable(functions, 'adminDelistStoreItem');
const configureStakeableNftCallable = httpsCallable(functions, 'configureStakeableNft');
const addPartnerCallable = httpsCallable(functions, 'addPartner');
const updatePartnerStatusCallable = httpsCallable(functions, 'updatePartnerStatus');
const approvePartnerPoolCallable = httpsCallable(functions, 'approvePartnerPool');
const blacklistPlayerCallable = httpsCallable(functions, 'blacklistPlayer');
const unblacklistPlayerCallable = httpsCallable(functions, 'unblacklistPlayer');
const adminSendNftCallable = httpsCallable(functions, 'adminSendNft');
const adminSendTokenCallable = httpsCallable(functions, 'adminSendToken');
const adminAirdropFromPastLeaderboardCallable = httpsCallable(functions, 'adminAirdropFromPastLeaderboard');
const HALVING_SCHEDULE = [
  { date: "2026-01-01", multiplier: 0.5 },   // Jan 2026: 50%
  { date: "2027-01-01", multiplier: 0.25 },  // Jan 2027: 25%
  { date: "2028-01-01", multiplier: 0.125 }  // Jan 2028: 12.5%
];

const getEffectiveApr = (baseApr) => {
  const nowMillis = Date.now();
  let currentMultiplier = 1.0;
  for (const phase of HALVING_SCHEDULE) {
    const phaseTime = new Date(phase.date + "T00:00:00Z").getTime();
    if (nowMillis >= phaseTime) {
      currentMultiplier = phase.multiplier;
    }
  }
  return baseApr * currentMultiplier;
};

const calculateStakeRewardsPure = (stake, currentTime) => {
  const baseApr = (stake.apr !== undefined && stake.apr !== null) ? stake.apr : 10;
  const effectiveApr = getEffectiveApr(baseApr);

  let lastCalc;
  if (stake.lastRewardCalculationTime && typeof stake.lastRewardCalculationTime.toMillis === 'function') {
    lastCalc = stake.lastRewardCalculationTime.toMillis();
  } else if (stake.lastRewardCalculationTime) {
    lastCalc = stake.lastRewardCalculationTime;
  } else if (stake.stakedAt && typeof stake.stakedAt.toMillis === 'function') {
    lastCalc = stake.stakedAt.toMillis();
  } else {
    lastCalc = stake.stakedAt || currentTime;
  }

  const timeDiff = currentTime - lastCalc;
  if (timeDiff <= 0) {
    return { newRewards: 0, totalRewards: stake.accruedRewards || 0, lastCalculationTime: lastCalc };
  }

  const millisecondsPerYear = 31536000000;
  const newRewards = (stake.stakedAmount * (effectiveApr / 100) * timeDiff) / millisecondsPerYear;

  return {
    newRewards: newRewards,
    totalRewards: (stake.accruedRewards || 0) + newRewards,
    effectiveApr: effectiveApr
  };
};

// ==================================================================================
// 1. SESSION KIT INITIALIZATION 
// (MUST be outside the App function to prevent resetting on every render)
// ==================================================================================
class DirectCloudWalletPlugin extends WalletPluginCloudWallet {
  async login(context) {
    if (!context.arbitrary) context.arbitrary = {};
    if (!context.arbitrary['nonce']) {
      context.arbitrary['nonce'] = `LOGIN-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
    }
    const nonce = context.arbitrary['nonce'];
    const loginRes = await this.waxLogin(context);
    this.data.loginNonce = nonce;
    return loginRes;
  }

  async sign(resolved, context) {
    try {
      return await super.sign(resolved, context);
    } catch (err) {
      console.error('[DirectCloudWalletPlugin] Signing error caught:', err);
      const msg = (err && err.message && !err.message.includes('has only a getter'))
        ? err.message
        : (typeof err === 'string' ? err : 'Transaction signing was cancelled or rejected by Cloud Wallet');
      const cleanErr = new Error(msg);
      cleanErr.original = err;
      throw cleanErr;
    }
  }
}

// ============================================================================
// 🛡️ Session.prototype.transact Global Safe Wrapper
// Ensures transaction execution catches raw DOMExceptions, string rejections, or
// unhandled Antelope RPC errors and returns a clean Error instance with a readable .message.
// ============================================================================
if (Session && Session.prototype && !Session.prototype._patchedTransact) {
  const originalTransact = Session.prototype.transact;
  Session.prototype._patchedTransact = true;
  Session.prototype.transact = async function (args, options) {
    try {
      return await originalTransact.call(this, args, options);
    } catch (err) {
      console.error('[Session.transact error intercepted]:', err);
      let msg = '';
      if (err && typeof err === 'object') {
        if (err.response?.json?.error?.details?.[0]?.message) {
          msg = err.response.json.error.details[0].message;
        } else if (err.response?.json?.error?.what) {
          msg = err.response.json.error.what;
        } else if (err.message && !err.message.includes('has only a getter')) {
          msg = err.message;
        } else if (err.toString && err.toString() !== '[object Object]') {
          msg = err.toString();
        }
      } else if (typeof err === 'string') {
        msg = err;
      }
      if (!msg || msg.includes('has only a getter')) {
        msg = 'Transaction cancelled, timed out, or rejected by WAX Cloud Wallet';
      }
      const cleanError = new Error(msg);
      cleanError.original = err;
      throw cleanError;
    }
  };
}

const webRenderer = new WebRenderer();

// In the app, WHATIF Wallet opens in the phone's browser and answers through our link. Google
// sign-in signs through it too.
const whatifWalletOptions = {
  loginNonce: () => takeLoginNonce(),
  ...(Capacitor.isNativePlatform() ? { returnUrl: 'app.rewardsinwhatif.game://login-callback', openUrl: (url) => Browser.open({ url }) } : {})
};
setGoogleWalletOptions(whatifWalletOptions);

const walletPlugins = [
  new WalletPluginAnchor(),
  new WhatifWalletPlugin(whatifWalletOptions),
];
if (!Capacitor.isNativePlatform()) {
  // WAX Cloud Wallet only works on Chrome / web browser; removed from Android app
  walletPlugins.unshift(new DirectCloudWalletPlugin());
  walletPlugins.push(new WalletPluginScatter());
}

const sessionKit = new SessionKit({
  appName: "WHATIF Battle For Riches",
  chains: [{
    id: "1064487b3cd1a897ce03ae5b6a865651747e2e152090f99c1d19d44e01aea5a4",
    url: "https://wax.greymass.com"
  }],
  ui: webRenderer,
  walletPlugins: walletPlugins,
});
// ============================================================================
// 🛡️ EXTENSION CONSOLE SANITIZER
// Filters out noisy MaxListenersExceeded & ObjectMultiplex warnings 
// generated by third-party Web3 wallet extension content scripts.
// ============================================================================
if (typeof window !== "undefined") {
  const originalWarn = console.warn;
  const originalError = console.error;

  const shouldIgnore = (message) => {
    if (typeof message !== "string") return false;
    return (
      message.includes("MaxListenersExceededWarning") ||
      message.includes("ObjectMultiplex") ||
      message.includes("background-liveness") ||
      message.includes("app-init-liveness")
    );
  };

  console.warn = function (...args) {
    if (args.length > 0 && shouldIgnore(args[0])) {
      return; // Silently drop extension stream noise
    }
    originalWarn.apply(console, args);
  };

  console.error = function (...args) {
    if (args.length > 0 && shouldIgnore(args[0])) {
      return; // Silently drop extension stream noise
    }
    originalError.apply(console, args);
  };
}

function App() {
  useAdsSwitch();
  const [session, setSession] = useState(null);
  const [pendingSession, setPendingSession] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [playerData, setPlayerData] = useState(null);
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Stability refs to prevent infinite re-render and re-verification loops
  const sessionRef = useRef(null);
  sessionRef.current = session;
  const pendingSessionRef = useRef(null);
  pendingSessionRef.current = pendingSession;
  const isVerifyingRef = useRef(false);
  const autoLoginAttempted = useRef(false);
  // 🛠️ FIX: ADD MISSING STATE VARIABLES HERE
  const [quest, setQuest] = useState(null);
  const [isWheelOpen, setIsWheelOpen] = useState(false);
  const [isAndroidPromoOpen, setIsAndroidPromoOpen] = useState(false);


  // Game State
  const [vaultStakes, setVaultStakes] = useState([]);
  const [vaultTotalValueLocked, setVaultTotalValueLocked] = useState({});
  const [vaultUserBoosts, setVaultUserBoosts] = useState({ gang_boost_scaled: 0, burn_boost_expires: 0 });
  const [equippedGear, setEquippedGear] = useState({});
  const [activeBoosts, setActiveBoosts] = useState({});
  const [tokenBalances, setTokenBalances] = useState({});
  const [areBalancesLoaded, setAreBalancesLoaded] = useState(false);
  const [indexPower, setIndexPower] = useState(0);
  const [onChainPlayerStats, setOnChainPlayerStats] = useState({ total_mp: 0 });

  // UI & Forms
  const [currentPage, setCurrentPage] = useState(() => {
    const path = window.location.pathname.replace('/', '');
    const validPages = ['profile', 'game', 'leaderboard', 'store', 'blending', 'unbox', 'hilo', 'vault', 'staking', 'gangs', 'about', 'partner', 'admin', 'terms', 'privacy'];
    if (isNative && ['vault', 'staking', 'partner', 'hilo'].includes(path)) {
      return 'leaderboard';
    }
    return validPages.includes(path) ? path : 'leaderboard';
  });
  const [leaderboardView, setLeaderboardView] = useState('daily');
  // Which season is actually claimable, read from the chain rather than typed
  // in by the player. seasonstates is the only authority on this: Firestore's
  // seasonNumber counts differently and is not what claimreward expects.
  const [seasonClaim, setSeasonClaim] = useState(null);

  const handleMpBoost = async () => {
    if (!session) {
      setBoostMpFeedback('You must be logged in to purchase an MP boost.');
      return;
    }

    setIsBoostingMp(true);
    setBoostMpFeedback('Initiating MP Boost purchase...');

    try {
      const quantity = '100000.00000 FIREMAN';
      const memo = `MP_BOOST::${session.actor.toString()}`;

      const actions = [{
        account: tokenContracts['FIREMAN'],
        name: 'transfer',
        authorization: [session.permissionLevel],
        data: {
          from: session.actor.toString(),
          to: MASTER_ACCOUNT_WAX_ID,
          quantity: quantity,
          memo: memo,
        },
      }];

      setBoostMpFeedback('Please approve the transfer in your wallet...');
      const txResult = await session.transact({ actions });
      const txId = txResult.response.transaction_id;

      setBoostMpFeedback('Transaction sent! Verifying activation...');
      await new Promise(r => setTimeout(r, 8000));

      const result = await purchaseMpBoostCallable({ txId });
      setBoostMpFeedback(`✅ ${result.data.message}`);

    } catch (error) {
      console.error('Error purchasing MP boost:', error);
      setBoostMpFeedback(`Failed: ${error.message}`);
    } finally {
      setIsBoostingMp(false);
      setTimeout(() => setBoostMpFeedback(''), 5000);
    }
  };

  // Admin State
  const [adminTargetPlayer, setAdminTargetPlayer] = useState('');
  const [adminTargetPlayerStatus, setAdminTargetPlayerStatus] = useState(null);
  const [sendNftTarget, setSendNftTarget] = useState('');
  const [sendAssetId, setSendAssetId] = useState('');
  const [sendNftMemo, setSendNftMemo] = useState('Admin manual distribution.');
  const [sendNftFeedback, setSendNftFeedback] = useState('');
  const [isSendingNft, setIsSendingNft] = useState(false);
  const [sendTokenTarget, setSendTokenTarget] = useState('');
  const [sendTokenAmount, setSendTokenAmount] = useState('');
  const [sendTokenSymbol, setSendTokenSymbol] = useState(TOKENS[0] || '');
  const [sendTokenMemo, setSendTokenMemo] = useState('Admin token distribution.');
  const [sendTokenFeedback, setSendTokenFeedback] = useState('');
  const [isSendingToken, setIsSendingToken] = useState(false);
  const [adminLoadingStatus, setAdminLoadingStatus] = useState(false);
  const [isResettingStamina, setIsResettingStamina] = useState(false);
  const [resetStaminaFeedback, setResetStaminaFeedback] = useState('');
  const [airdropDate, setAirdropDate] = useState('');
  const [airdropAssetIds, setAirdropAssetIds] = useState('');
  const [airdropFeedback, setAirdropFeedback] = useState('');
  const [isAirdropping, setIsAirdropping] = useState(false);
  const [listItemId, setListItemId] = useState('');
  const [listItemPrice, setListItemPrice] = useState('');
  const [listItemToken, setListItemToken] = useState('FIREMAN');

  // Vault/Staking State (on-chain)
  const [selectedVaultToken, setSelectedVaultToken] = useState('FIREMAN');
  const [vaultStakeAmountInput, setVaultStakeAmountInput] = useState('');
  const [vaultLockPeriodInput, setVaultLockPeriodInput] = useState('flexible');
  const [vaultStakingFeedback, setVaultStakingFeedback] = useState('');
  const [isInitiatingVaultStake, setIsInitiatingVaultStake] = useState(false);

  // Gangs & Other
  const [loginRewardMessage, setLoginRewardMessage] = useState('');
  const [gangData, setGangData] = useState(null);
  const [allGangs, setAllGangs] = useState([]);
  const [gangsLoading, setGangsLoading] = useState(true);
  const [isResettingDailyPoints, setIsResettingDailyPoints] = useState(false);
  const [resetDailyPointsFeedback, setResetDailyPointsFeedback] = useState('');
  const [isResettingAllWeekly, setIsResettingAllWeekly] = useState(false);
  const [resetAllWeeklyFeedback, setResetAllWeeklyFeedback] = useState('');
  const [manualPayoutSeasonId, setManualPayoutSeasonId] = useState('');
  const [manualPayoutFeedback, setManualPayoutFeedback] = useState('');
  const [isManualPayoutProcessing, setIsManualPayoutProcessing] = useState(false);
  const [staminaData, setStaminaData] = useState({});
  const [isPartner, setIsPartner] = useState(false);
  const [partnerMPBoost, setPartnerMPBoost] = useState(0);
  const [isBoostingMp, setIsBoostingMp] = useState(false);
  const [boostMpFeedback, setBoostMpFeedback] = useState('');

  // --- 2. REPLACE fetchNfts IN src/App.js (Under Helper Functions) ---

  const fetchNfts = useCallback(async (accountName) => {
    if (!accountName) return;
    setLoading(true);

    // Primary schemas specific to core WHATIF & Partner assets
    const coreSchemas = ['fireman', 'gear', 'deeds', 'emblems', 'gangpass', 'packs', 'universe', 'support', 'inspiration', 'encouragemen', 'heroes', 'verified', 'verrified'].join(',');

    // Full list of 12 supported ecosystem collections
    const collectionsToScan = Array.isArray(SUPPORTED_COLLECTIONS) && SUPPORTED_COLLECTIONS.length > 0
      ? SUPPORTED_COLLECTIONS
      : [
        'whatifreward', 'idaretodream', 'waxstation21', 'syndicates21', 'cleanupcentr',
        'pixeljourney', 'pantheon', 'alien.worlds', 'planetdefnft',
        'futuresrelic', 'facings', 'mlb.topps', 'taco'
      ];

    let allResults = [];
    const seenAssetIds = new Set();
    const appendUnique = (assets) => {
      const added = [];
      for (const item of (assets || [])) {
        const id = item?.asset_id ? String(item.asset_id) : null;
        if (id) {
          if (!seenAssetIds.has(id)) {
            seenAssetIds.add(id);
            added.push(item);
          }
        } else {
          added.push(item);
        }
      }
      return added;
    };

    console.log("🛡️ Starting Robust Multi-Collection Chain Scan...");

    // ⚡ Fast Priority Fetch: Instantly load WHATIF Verified Master Keycard (#909865)
    try {
      for (const endpoint of ATOMIC_API_ENDPOINTS) {
        try {
          const directKcUrl = `${endpoint}/atomicassets/v1/assets?owner=${accountName}&collection_name=whatifreward&template_id=909865&limit=10`;
          const kcRes = await fetch(directKcUrl);
          if (kcRes.ok) {
            const kcJson = await kcRes.json();
            if (kcJson.success && kcJson.data && kcJson.data.length > 0) {
              const uniqueKc = appendUnique(kcJson.data);
              allResults = [...uniqueKc];
              setNfts([...allResults]);
              break;
            }
          }
        } catch (e) { /* try next endpoint */ }
      }
    } catch (e) {
      console.warn("Direct keycard prefetch skipped:", e.message);
    }

    for (const collectionName of collectionsToScan) {
      let page = 1;
      let hasMore = true;

      // Only filter by schema for core WHATIF/I Dare To Dream collections
      // External partner collections (e.g. alien.worlds, taco) use their own schemas
      const isCoreCollection = ['whatifreward', 'idaretodream'].includes(collectionName);
      const schemaParam = isCoreCollection ? `&schema_name=${coreSchemas}` : '';

      while (hasMore) {
        let pageSuccess = false;

        for (const endpoint of ATOMIC_API_ENDPOINTS) {
          try {
            const url = `${endpoint}/atomicassets/v1/assets?owner=${accountName}&collection_name=${collectionName}${schemaParam}&page=${page}&limit=250`;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);

            const response = await fetch(url, { signal: controller.signal });
            clearTimeout(timeoutId);

            if (response.status === 429) {
              console.warn(`⚠️ Rate limited (429) by ${endpoint}. Rotating node...`);
              continue;
            }

            if (!response.ok) throw new Error(`Status ${response.status}`);

            const json = await response.json();
            if (json.success) {
              const data = json.data || [];
              const uniqueData = appendUnique(data);
              allResults = [...allResults, ...uniqueData];
              if (data.length < 250) hasMore = false;
              pageSuccess = true;
              break;
            }
          } catch (e) {
            console.warn(`❌ Node ${endpoint} failed for ${collectionName}:`, e.message);
          }
        }

        if (!pageSuccess) {
          console.error(`❌ Could not fetch ${collectionName} page ${page}`);
          hasMore = false;
        }

        // API rate-limit delay
        await new Promise((resolve) => setTimeout(resolve, 350));
        page++;
      }
    }

    setNfts([...allResults]);
    setLoading(false);
  }, []);

  // --- ADDED WAX RPC ENDPOINTS FOR FALLBACK ---
  const WAX_RPC_ENDPOINTS = [
    "https://wax.greymass.com",
    "https://api.wax.alohaeos.com",
    "https://wax.eosphere.io",
    "https://wax.eosdac.io",
    "https://wax.eu.eosamsterdam.net",
    "https://api.waxsweden.org"
  ];

  // 2. Fetch Token Balances (ROBUST FALLBACK VERSION)
  const fetchTokenBalances = useCallback(async (activeSession) => {
    if (!activeSession) return;
    setAreBalancesLoaded(false);
    const accountName = activeSession.actor.toString();

    try {
      const balancePromises = TOKENS.map(async (token) => {
        const contract = tokenContracts[token] || TOKEN_CONTRACT;
        const decimals = tokenDecimals[token] ?? 8;
        let finalBalanceString = `${Number(0).toFixed(decimals)} ${token}`; // Default to 0

        // Loop through our nodes until one works!
        for (const endpoint of WAX_RPC_ENDPOINTS) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout per node

            const response = await fetch(`${endpoint}/v1/chain/get_currency_balance`, {
              method: 'POST',
              headers: { 'Content-Type': 'text/plain' }, // Bypass strict CORS
              body: JSON.stringify({
                code: contract,
                account: accountName,
                symbol: token
              }),
              signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) continue; // If this node failed, try the next one in the array

            const data = await response.json();
            if (data && data.length > 0) {
              finalBalanceString = data[0]; // Success! e.g. "150.00000 FIREMAN"
            }
            break; // Node worked, stop the loop for this specific token!

          } catch (err) {
            // Timeout or network error, silently continue to the next endpoint
          }
        }
        return { token, balanceString: finalBalanceString };
      });

      const results = await Promise.all(balancePromises);
      const balances = {};
      results.forEach(result => { balances[result.token] = result.balanceString; });

      setTokenBalances(balances);

      try {
        let ipFound = false;
        for (const endpoint of WAX_RPC_ENDPOINTS) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            const ipResponse = await fetch(`${endpoint}/v1/chain/get_table_rows`, {
              method: 'POST',
              body: JSON.stringify({
                json: true,
                code: "whatifcnvrgx",
                scope: "whatifcnvrgx",
                table: "councils",
                lower_bound: accountName,
                upper_bound: accountName,
                limit: 1
              }),
              signal: controller.signal
            });
            clearTimeout(timeoutId);
            if (!ipResponse.ok) continue;
            const ipRes = await ipResponse.json();
            
            if (ipRes && ipRes.rows && ipRes.rows.length > 0 && ipRes.rows[0].owner === accountName) {
                setIndexPower(parseFloat(ipRes.rows[0].index_power));
                ipFound = true;
            }
            break;
          } catch (err) { }
        }
        if (!ipFound) setIndexPower(0);
      } catch (e) {
        console.error("Index Power Error", e);
        setIndexPower(0);
      }

      setAreBalancesLoaded(true); // Allow the UI to finish loading
    } catch (error) {
      console.error("Error fetching token balances:", error);
      setTokenBalances({});
      setAreBalancesLoaded(true); // Still set to true so the site loads even if balances fail completely
    }
  }, []);

  const handleLogin = async () => {
    const loginOptions = Capacitor.isNativePlatform()
      ? { returnUrl: 'app.rewardsinwhatif.game://login-callback' }
      : {};

    try {
      setLoading(true);
      prefetchLoginNonce();
      // 1. Connect the wallet. Opens Popup #1 safely.
      const result = await sessionKit.login(loginOptions);

      if (result && result.session) {
        if (result.session.walletPlugin?.id === 'cloudwallet') {
          // 2. Cloud Wallet signs a one-time login proof from the VERIFY IDENTITY button: its
          // signing popup needs that click, or the browser blocks it.
          result.session.isCloudWallet = true;
          setPendingSession(result.session);
        } else {
          // 2. No other wallet has anything more to tap: WHATIF Wallet and Anchor already proved
          // the account, and a browser-extension wallet signs the proof straight away.
          await verifyIdentity(result.session, connectProofOf(result));
        }
      }
    } catch (error) {
      console.error("Login failed:", error);
      if (sessionKit) await sessionKit.logout().catch(() => {});
      setPendingSession(null);
      alert("Connection Failed: " + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  // 3. LOAD DATA (Internal Helper)
  const loadGameData = useCallback(async (currentSession) => {
    // CRITICAL FIX: Do not load data if Firebase isn't ready. 
    // This stops the "Missing Permissions" error.
    if (!currentSession || !auth.currentUser) {
      console.log("⏳ Skipping data load: Auth not ready.");
      return;
    }

    const accountName = currentSession.actor.toString();

    console.log("📂 Loading Player Data for", accountName);

    // Load NFTs and Tokens in parallel
    await Promise.all([
      fetchNfts(accountName),
      fetchTokenBalances(currentSession)
    ]);

    const playerDocRef = doc(db, "players", accountName);
    try {
      const playerDocSnap = await getDoc(playerDocRef);

      if (!playerDocSnap.exists()) {
        console.log("🆕 Creating New Profile...");
        const newPlayerData = {
          level: 1, xp: 0, xpForNextLevel: 1000,
          leaderboardPoints: 0, dailyPoints: 0, rank: "Unranked", questsCompleted: 0,
          staminaUsage: {}, revivesUsedThisWeek: 0, equippedDeedId: null, equippedGear: {},
          quest: null, deedsHealth: {},
          isBlacklisted: false, lastLoginTimestamp: null, dailyBoost: null,
          dailyWithdrawal: { amount: 0, timestamp: null },
          vault_summary: { totalBoost: 0 }, vaultScore: 0,
        };
        await setDoc(playerDocRef, newPlayerData);
        setPlayerData(newPlayerData);
      } else {
        setPlayerData(playerDocSnap.data());
      }
    } catch (dbError) {
      console.error("Database Error:", dbError);
    }
  }, [fetchNfts, fetchTokenBalances]);

  const verifyIdentity = useCallback(async (targetSession, connectProof) => {
    const activePending = typeof targetSession?.transact === 'function'
      ? targetSession
      : (pendingSessionRef.current || pendingSession);
    if (!activePending) return;

    const walletAddress = String(activePending.actor);

    // Fast-path: If Firebase Auth is already signed in for this user, activate immediately
    if (auth.currentUser && auth.currentUser.uid === walletAddress) {
      console.log(`Already authenticated as ${walletAddress}, activating session.`);
      setSession(activePending);
      setPendingSession(null);
      loadGameData(activePending);
      return;
    }

    if (isVerifyingRef.current) {
      console.log("Verification already in progress for", walletAddress);
      return;
    }
    isVerifyingRef.current = true;
    setLoading(true);

    try {
      const verifyWaxLoginCallable = httpsCallable(functions, 'verifyWaxLogin');
      let response;

      // Google, WHATIF Wallet and Anchor proved the account as they connected. If the server
      // turns that down, the wallet signs a login proof instead.
      if (connectProof) {
        response = await verifyWaxLoginCallable({ wallet: walletAddress, connectProof }).catch((proofErr) => {
          console.warn("Connection proof not accepted:", proofErr);
          return null;
        });
      }

      if (!response) {
        // 1. Prepare Dummy Transaction (never broadcast) carrying a one-time nonce from the
        // server so the signed proof can't be replayed
        const nonce = await takeLoginNonce();
        const action = {
          account: "eosio.token",
          name: "transfer",
          authorization: [activePending.permissionLevel],
          data: {
            from: walletAddress,
            to: "whatifreward",
            quantity: "0.00000001 WAX",
            memo: `LOGIN-AUTH-${nonce}`
          }
        };

        // 2. Fire transaction with rotating RPC fallback (5-minute expiry for mobile switching)
        const rpcCandidates = [
          "https://wax.greymass.com",
          "https://api.wax.alohaeos.com",
          "https://wax.eosphere.io",
          "https://wax.eosdac.io",
          "https://wax.eu.eosamsterdam.net"
        ];
        let signResult = null;
        let lastSignError = null;

        for (const rpcUrl of rpcCandidates) {
          try {
            if (activePending.chain) {
              activePending.chain.url = rpcUrl;
            }
            signResult = await activePending.transact(
              { actions: [action] },
              { broadcast: false, expireSeconds: 300 }
            );
            if (signResult) break;
          } catch (sErr) {
            console.warn(`Transact failed on ${rpcUrl}:`, sErr);
            lastSignError = sErr;
            // Only a network problem is worth another node: not the player saying no, or anything the wallet answered
            if (/cancel|reject|closed|abort|wallet|passkey/i.test(String(sErr?.message || sErr))) {
              throw sErr;
            }
          }
        }
        if (!signResult) throw lastSignError || new Error("Wallet failed to sign.");

        // 3. Extract Data needed for Backend (Cloud Wallet may add cosigner signatures; send all)
        const signatures = signResult.signatures.map(String);
        let rawBytes = null;
        if (signResult.resolved?.serializedTransaction) {
          rawBytes = signResult.resolved.serializedTransaction;
        } else if (signResult.resolved?.resolvedTransaction?.serialized) {
          rawBytes = signResult.resolved.resolvedTransaction.serialized;
        } else if (signResult.transaction?.serialized) {
          rawBytes = signResult.transaction.serialized;
        }
        if (!rawBytes) throw new Error("Could not extract transaction data.");

        const serializedTxHex = Array.from(new Uint8Array(rawBytes))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');

        console.log("Submitting to Backend...", { wallet: walletAddress });

        // 4. Send to Cloud Function
        response = await verifyWaxLoginCallable({
          wallet: walletAddress,
          signatures,
          serializedTxHex: serializedTxHex
        });
      }

      // 5. Force Mobile Persistence & Sign in with Firebase Custom Token
      const firebaseToken = response.data.token;
      await signInWithCustomToken(auth, firebaseToken);

      console.log(`Login Success! Authenticated as ${walletAddress}`);

      // 6. Finally, move the session out of holding and into the active app.
      setSession(activePending);
      setPendingSession(null);

      // 7. Force Data Load 
      loadGameData(activePending);

    } catch (error) {
      console.error("Verification failed:", error);
      alert(`Authentication Failed: ${error.message}`);
      if (activePending.isGoogleWallet) {
        clearGoogleSession();
        setPendingSession(null);
      } else {
        // Anchor/external wallets can simply tap "VERIFY IDENTITY" to retry
        setPendingSession(activePending);
      }
    } finally {
      isVerifyingRef.current = false;
      setLoading(false);
    }
  }, [loadGameData, pendingSession]);

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      const result = await loginWithGoogle();
      if (!result?.session) throw new Error("Google login cancelled or failed");
      // Google proved the account: our server signs the player in, with no wallet to open
      await verifyIdentity(result.session, result.connectProof);
      console.log(`Google Login Success! Authenticated as ${result.waxAccount}`);
    } catch (error) {
      console.error("Google Login failed:", error);
      alert("Google Connection Failed: " + (error.message || error));
      setPendingSession(null);
    } finally {
      setLoading(false);
    }
  };

  const [showReviewerModal, setShowReviewerModal] = useState(false);
  const [reviewerUsername, setReviewerUsername] = useState('reviewer@rewardsinwhatif.app');
  const [reviewerPassword, setReviewerPassword] = useState('WhatifReview2026!');
  const [reviewerLoading, setReviewerLoading] = useState(false);
  const [reviewerError, setReviewerError] = useState('');

  const handleReviewerLogin = async (e) => {
    if (e) e.preventDefault();
    setReviewerLoading(true);
    setReviewerError('');
    try {
      const reviewerLoginCallable = httpsCallable(functions, 'reviewerLogin');
      const res = await reviewerLoginCallable({
        username: reviewerUsername.trim(),
        password: reviewerPassword.trim()
      });
      if (!res.data?.success || !res.data?.token) {
        throw new Error(res.data?.message || 'Login failed');
      }
      const { token, account } = res.data;
      await signInWithCustomToken(auth, token);
      const rSession = createReviewerSession(account);
      setSession(rSession);
      setPendingSession(null);
      setShowReviewerModal(false);
      loadGameData(rSession);
    } catch (err) {
      console.error('Reviewer login error:', err);
      setReviewerError(err.message || 'Invalid reviewer credentials');
    } finally {
      setReviewerLoading(false);
    }
  };

  // 7. DATA SYNC (Runs when both sessions are ready)
  useEffect(() => {
    // Only load data if we have a Wallet Session AND are logged into Firebase
    if (session && firebaseUser) {
      loadGameData(session);

      // Try to claim daily login reward silently
      if (typeof claimDailyLoginRewardCallable === 'function') {
        claimDailyLoginRewardCallable()
          .then(res => {
            // If true, they won! Show the UI message.
            if (res?.data?.success) {
              setLoginRewardMessage(res.data.message);
            } else {
              // If false, just log it silently for your own debugging
              console.log("Daily Login Check:", res?.data?.message);
            }
          })
          .catch((error) => {
            console.error("Failed to check daily login:", error);
          });
      }
    }
  }, [session, firebaseUser, loadGameData]);
  useEffect(() => {
    if (playerData?.gangId) {
      const gangRef = doc(db, "gangs", playerData.gangId);
      const unsub = onSnapshot(gangRef, (docSnap) => {
        setGangData(docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null);
      });
      return () => unsub();
    } else {
      setGangData(null);
    }
  }, [playerData?.gangId]);
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      console.log("🔥 Firebase Auth:", user ? `Logged In as ${user.uid}` : "Logged Out");
      setFirebaseUser(user);
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  // Listen for Deep Links (WAX Cloud Wallet Redirects on Native Android)
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      let sub = null;
      import('@capacitor/app').then(({ App: CapApp }) => {
        CapApp.addListener('appUrlOpen', (data) => {
          console.log('App opened with URL:', data.url);
          // WHATIF Wallet's answer comes back on this link too: hand it over first
          if (handleWhatifWalletReturn(data.url)) return;
          if (data.url && data.url.includes('app.rewardsinwhatif.game://login-callback')) {
            const url = new URL(data.url);
            window.location.href = `/${url.search}${url.hash}`;
          }
        }).then(listener => { sub = listener; });
      }).catch(e => {
        console.warn("Capacitor App plugin error:", e);
      });
      return () => {
        if (sub && sub.remove) sub.remove();
      };
    }
  }, []);

  const handleSessionFound = useCallback(async (activeSession) => {
    if (!activeSession) return;
    if (activeSession && activeSession.chain && typeof activeSession.chain.url === 'string' && activeSession.chain.url.includes("eosamsterdam")) {
      activeSession.chain.url = "https://wax.greymass.com";
    }
    const actorStr = activeSession.actor.toString();

    // Prevent re-triggering if this session is already active
    if (sessionRef.current && sessionRef.current.actor && sessionRef.current.actor.toString() === actorStr) {
      return;
    }

    console.log("💰 Wallet found:", actorStr);
    // Firebase takes a moment to load a saved sign-in. Checking sooner treats every returning
    // player as signed out.
    await auth.authStateReady();
    if (auth.currentUser && auth.currentUser.uid === actorStr) {
      setSession(activeSession);
      setPendingSession(null);
    } else if (activeSession.isGoogleWallet) {
      // Their sign-in here has ended: tapping Google again is all it takes
      clearGoogleSession();
    } else {
      console.log("Wallet found, awaiting auth alignment for", actorStr);
      setPendingSession(prev => {
        if (prev && prev.actor && prev.actor.toString() === actorStr) {
          return prev;
        }
        return activeSession;
      });
      setSession(null);
      if (activeSession.isCloudWallet || activeSession.walletPlugin?.id === 'cloudwallet') {
        verifyIdentity(activeSession);
      }
    }
  }, [verifyIdentity]);

  // Sync session and Firebase Auth alignment
  useEffect(() => {
    if (pendingSession && firebaseUser && firebaseUser.uid === pendingSession.actor.toString()) {
      console.log("Verified auth matched for", pendingSession.actor.toString());
      setSession(pendingSession);
      setPendingSession(null);
    } else if (session && firebaseUser && firebaseUser.uid !== session.actor.toString()) {
      console.warn(`Mismatch detected: session is ${session.actor.toString()} but Firebase Auth is ${firebaseUser.uid}.`);
      auth.signOut().then(() => {
        setPendingSession(session);
        setSession(null);
      });
    }
  }, [session, pendingSession, firebaseUser]);

  // D. AUTO-LOGIN (Executes exactly once on initial load)
  useEffect(() => {
    if (autoLoginAttempted.current) return;
    autoLoginAttempted.current = true;

    const autoLogin = async () => {
      try {
        const googleSess = await restoreGoogleSession();
        if (googleSess) {
          await handleSessionFound(googleSess);
          return;
        }
        const restored = await sessionKit.restore();
        if (restored) {
          await handleSessionFound(restored);
        }
      } catch (e) { console.error("Auto-login failed:", e); }
    };
    autoLogin();
  }, [handleSessionFound]);

  // Balances, NFTs, passkeys and backup codes live in WHATIF Wallet
  const openWalletPage = () => {
    const url = "https://wallet.rewardsinwhatif.app/";
    if (Capacitor.isNativePlatform()) Browser.open({ url });
    else window.open(url, "_blank", "noopener");
  };

  const handleLogout = useCallback(async () => {
    try {
      await sessionKit.logout();
      clearGoogleSession();
      await auth.signOut();
      setSession(null);
      setPendingSession(null); // Clear holding area on logout
      setFirebaseUser(null);
      setPlayerData(null);
      setNfts([]);
      setEquippedGear({});
      setQuest(null);
      console.log("Logged out.");
    } catch (e) {
      console.error("Logout Error:", e);
    }
  }, []);

  // ... PASTE THE REST OF YOUR APP FUNCTIONS BELOW (handleResetAllDailyPoints, etc.) ...

  const handleResetAllDailyPoints = async () => {
    setIsResettingDailyPoints(true);
    setResetDailyPointsFeedback('Processing reset...');
    try {
      const result = await adminResetAllDailyPointsCallable();
      setResetDailyPointsFeedback(result.data.message || 'Daily points have been reset successfully.');
    } catch (error) {
      console.error("Error resetting all daily points:", error);
      setResetDailyPointsFeedback(`Error: ${error.message}`);
    } finally {
      setIsResettingDailyPoints(false);
    }
  };

  const navigateTo = (page) => {
    setCurrentPage(page);
    window.history.pushState({}, '', `/${page === 'leaderboard' ? '' : page}`);
  };


  const rarityColors = {
    'Common': '#9e9e9e',
    'Uncommon': '#4caf50',
    'Rare': '#2196f3',
    'Epic': '#9c27b0',
    'Legendary': '#ff9800',
    'Mystic': '#f44336',
    'Super Mystic': '#b71c1c',
    'Ascended Super Mystic': '#e53935',
  };
  // in src/App.js
  // ... after your useState hooks and rarityColors constant ...
  // Firebase Functions Callables - WRAPPED IN useCallback FOR STABILITY
  // NOTE: `functions` is imported from firebase.js (same app instance as App Check) — do NOT redeclare with getFunctions() here.
  const configureStakeableNftCallable = useCallback(httpsCallable(functions, 'configureStakeableNft'), [functions]);
  const initiateNftStakeCallable = useCallback(httpsCallable(functions, 'initiateNftStake'), [functions]);
  const verifyNftStakeCallable = useCallback(httpsCallable(functions, 'verifyNftStake'), [functions]);
  const unstakeNftCallable = useCallback(httpsCallable(functions, 'unstakeNft'), [functions]);
  const blacklistPlayerCallable = useCallback(httpsCallable(functions, 'blacklistPlayer'), [functions]);
  const unblacklistPlayerCallable = useCallback(httpsCallable(functions, 'unblacklistPlayer'), [functions]);
  const adminSendNftCallable = useCallback(httpsCallable(functions, 'adminSendNft'), [functions]);
  const adminSendTokenCallable = useCallback(httpsCallable(functions, 'adminSendToken'), [functions]);
  // --- VAULT: On-Chain Bridge Callable ---
  const syncPlayerVaultStateCallable = useCallback(httpsCallable(functions, 'syncPlayerVaultState'), [functions]);
  // --- VAULT: Legacy claim-all (kept for backwards compat if needed) ---
  const claimAllVaultRewardsCallable = useCallback(httpsCallable(functions, 'claimAllVaultRewards'), [functions]);
  const addPartnerCallable = useCallback(httpsCallable(functions, 'addPartner'), [functions]);
  const updatePartnerStatusCallable = useCallback(httpsCallable(functions, 'updatePartnerStatus'), [functions]);
  const initiatePartnerPoolCallable = useCallback(httpsCallable(functions, 'initiatePartnerPool'), [functions]);
  const approvePartnerPoolCallable = useCallback(httpsCallable(functions, 'approvePartnerPool'), [functions]);
  const verifyPartnerDepositCallable = useCallback(httpsCallable(functions, 'verifyPartnerDeposit'), [functions]);

  const activateGangPassCallable = useCallback(httpsCallable(functions, 'activateGangPass'), [functions]);
  const createGangCallable = useCallback(httpsCallable(functions, 'createGang'), [functions]);
  const joinGangCallable = useCallback(httpsCallable(functions, 'joinGang'), [functions]);
  const leaveGangCallable = useCallback(httpsCallable(functions, 'leaveGang'), [functions]);
  const upgradeGangBuildingCallable = useCallback(httpsCallable(functions, 'upgradeGangBuilding'), [functions]);
  const claimFaucetCallable = useCallback(httpsCallable(functions, 'claimFaucet'), [functions]);
  const playHiLoCallable = useCallback(httpsCallable(functions, 'playHiLo'), [functions]);
  const playCrashCallable = useCallback(httpsCallable(functions, 'playCrash'), [functions]);
  const cashOutCrashCallable = useCallback(httpsCallable(functions, 'cashOutCrash'), [functions]);
  const crashStatusCallable = useCallback(httpsCallable(functions, 'crashStatus'), [functions]);
  const requestWithdrawalCallable = useCallback(httpsCallable(functions, 'requestWithdrawal'), [functions]);
  const verifyDepositCallable = useCallback(httpsCallable(functions, 'verifyDeposit'), [functions]);
  const claimDailyLoginRewardCallable = useCallback(httpsCallable(functions, 'claimDailyLoginReward'), [functions]);
  const depositToGangTreasuryCallable = useCallback(httpsCallable(functions, 'depositToGangTreasury'), [functions]);

  const adminAirdropFromPastLeaderboardCallable = useCallback(httpsCallable(functions, 'adminAirdropFromPastLeaderboard'), [functions]);
  const adminResetWeeklyPointsCallable = useCallback(httpsCallable(functions, 'adminResetWeeklyPoints'), [functions]);
  const adminRestoreWeeklyPointsCallable = useCallback(httpsCallable(functions, 'adminRestoreWeeklyPoints'), [functions]);
  const adminResetAllWeeklyPointsCallable = useCallback(httpsCallable(functions, 'adminResetAllWeeklyPoints'), [functions]);
  const adminResetAllDailyPointsCallable = useCallback(httpsCallable(functions, 'adminResetAllDailyPoints'), [functions]);
  const equipItemCallable = useCallback(httpsCallable(functions, 'equipItem'), [functions]);
  const adminConfigureUnboxCallable = useCallback(httpsCallable(functions, 'adminConfigureUnbox'), [functions]);
  const adminTroubleshootUnboxCallable = useCallback(httpsCallable(functions, 'adminTroubleshootUnbox'), [functions]);
  const adminSendWeeklyPayoutCallable = useCallback(httpsCallable(functions, 'adminSendWeeklyPayout'), [functions]);
  const adminSendWeeklyDiscordAnnouncementCallable = useCallback(httpsCallable(functions, 'adminSendWeeklyDiscordAnnouncement'), [functions]);
  const purchaseMpBoostCallable = useCallback(httpsCallable(functions, 'purchaseMpBoost'), [functions]);
  const isMaster = useMemo(() => {
    return session && ADMIN_ACCOUNTS.includes(session.actor.toString());
  }, [session]);
  const vaultTotalBoosts = useMemo(() => {
    let totalBoost = 0;
    if (playerData?.vault_summary) {
      for (const token in playerData.vault_summary) {
        totalBoost += (playerData.vault_summary[token].activeBoost || 0);
      }
    }
    return totalBoost;
  }, [playerData]);

  const maxStamina = useMemo(() => {
    if (!playerData) return BASE_MAX_STAMINA;
    const gearStaminaBoost = activeBoosts['Stamina Boost'] || 0;
    const stakedNftStaminaBoost = playerData.nftStakeBoosts?.['Stamina Boost'] || 0;
    const gangStaminaBoost = gangData?.gangBoosts?.['Stamina Boost'] || 0;
    const rawStaminaBoost = gearStaminaBoost + stakedNftStaminaBoost + gangStaminaBoost;

    const effectiveStaminaBoost = calculateDiminishedBoost(rawStaminaBoost);

    // --- NEW: BURN BOOST (+1 Flat Stamina) ---
    const now = Date.now();
    const burnStamina = (playerData.activeBoosts?.boost_stam_max > now) ? 10 : 0;
    // ----------------------------------------

    return Math.floor(BASE_MAX_STAMINA * (1 + effectiveStaminaBoost / 100)) + burnStamina;
  }, [activeBoosts, playerData, gangData]);
  const emblemNfts = useMemo(() => nfts.filter(nft => nft.schema.schema_name === 'emblems'), [nfts]);
  const gearNfts = useMemo(() => {
    const gear = {};
    nfts.forEach(nft => {
      const slot = itemTemplates[nft.template.template_id]?.slot;
      if (slot) {
        if (!gear[slot]) gear[slot] = [];
        gear[slot].push(nft);
      }
    });
    return gear;
  }, [nfts]);
  // In src/App.js
  // ... (code for gearNfts)

  const gangPassNfts = useMemo(() => nfts.filter(nft => nft.template.template_id === '899554'), [nfts]);

  // --- ADD THIS HOOK INSIDE function App() ---
  // This fetches the gang data so the Profile page can see the boosts
  useEffect(() => {
    if (playerData?.gangId) {
      const gangRef = doc(db, "gangs", playerData.gangId);
      const unsubscribe = onSnapshot(gangRef, (docSnap) => {
        if (docSnap.exists()) {
          // Store the gang data in App state
          setGangData({ id: docSnap.id, ...docSnap.data() });
        } else {
          setGangData(null);
        }
      });
      return () => unsubscribe();
    } else {
      setGangData(null);
    }
  }, [playerData?.gangId]);
  useEffect(() => {
    const onPopState = () => {
      const path = window.location.pathname.replace('/', '');
      // CHANGE: Default here is also 'leaderboard'
      setCurrentPage(path || 'leaderboard');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  useEffect(() => {
    const boosts = {};
    for (const slot in equippedGear) {
      const gear = equippedGear[slot];
      if (gear) {
        const template = itemTemplates[gear.template.template_id];
        if (template && template.boostType) {
          boosts[template.boostType] = (boosts[template.boostType] || 0) + template.boostValue;
        }
      }
    }
    setActiveBoosts(boosts);
  }, [equippedGear]);

  // ----------------------------------------------------------------------------
  // FIX 2: Stamina Usage Listener (Gated by firebaseUser)
  // ----------------------------------------------------------------------------
  useEffect(() => {
    // CRITICAL: Wait for Firebase Auth before listening to DB
    if (!session || !firebaseUser || nfts.length === 0) {
      setStaminaData({});
      return;
    }

    const ownedFiremanAssetIds = nfts
      .filter(nft => itemTemplates[nft.template.template_id]?.attack !== undefined)
      .map(nft => nft.asset_id);

    if (ownedFiremanAssetIds.length === 0) {
      setStaminaData({});
      return;
    }

    const chunk = (arr, size) =>
      Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
        arr.slice(i * size, i * size + size)
      );

    const chunks = chunk(ownedFiremanAssetIds, 30);
    const unsubscribes = [];

    chunks.forEach(assetIdChunk => {
      const q = query(collection(db, "nft_stamina_usage"), where(documentId(), "in", assetIdChunk));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const chunkStaminaData = {};
        snapshot.forEach(doc => {
          const docData = doc.data();
          chunkStaminaData[doc.id] = docData.usage_count || 0;
        });
        setStaminaData(prevData => ({ ...prevData, ...chunkStaminaData }));
      }, (error) => {
        // Silent fail on permission denied (waits for auth)
        if (error.code !== 'permission-denied') console.error("Stamina listener error:", error);
      });

      unsubscribes.push(unsubscribe);
    });

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [session, nfts, firebaseUser]); // <--- Added firebaseUser dependency
  useEffect(() => {
    if (!session) return;
    const playerDocRef = doc(db, "players", session.actor.toString());
    const stakesCollectionRef = collection(playerDocRef, "nft_stakes");

    // This onSnapshot is a real-time listener for player data
    const unsubscribePlayer = onSnapshot(playerDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setPlayerData(data);
      } else {
        console.log("No such document! Need to create one for new player.");
      }
    }, (error) => {
      console.warn("Player snapshot error:", error);
    });

    // Real-time listener for Firebase staked NFTs (only active if signed in as owner to prevent permission errors)
    let unsubscribeStakes = () => {};
    if (firebaseUser && firebaseUser.uid === session.actor.toString()) {
      unsubscribeStakes = onSnapshot(stakesCollectionRef, (snapshot) => {
        const stakes = [];
        snapshot.forEach((doc) => {
          stakes.push({ id: doc.id, ...doc.data() });
        });
        setFirebaseStakes(stakes);
      }, (error) => {
        console.warn("Stakes snapshot error:", error);
      });
    }

    // Clean up listeners when the user logs out or session changes
    return () => {
      unsubscribePlayer();
      unsubscribeStakes();
    };
  }, [session, firebaseUser]);


  const [equippedGearRaw, setEquippedGearRaw] = useState(null);

  // This hook runs ONCE to load the player's saved equipment from Firebase (legacy, keeping for backwards compat or until full migration)
  useEffect(() => {
    if (playerData && nfts.length > 0 && !hasLoaded) {

      setHasLoaded(true); // Mark initial load as complete
    }
  }, [playerData, nfts, hasLoaded]);

  // Hook to populate equippedGear from Firebase playerData, on-chain data, and user's NFTs
  useEffect(() => {
    const loadedGear = {};

    // First load from on-chain raw data (legacy backward compat)
    if (equippedGearRaw) {
      const mapIdToMockNft = (assetId, templateId, slot) => {
        if (assetId && String(assetId) !== "0" && templateId && String(templateId) !== "0") {
          loadedGear[slot] = {
            asset_id: String(assetId),
            template: { template_id: String(templateId) }
          };
        }
      };
      mapIdToMockNft(equippedGearRaw.attack_gear_id, equippedGearRaw.attack_gear_template_id, 'Sword');
      mapIdToMockNft(equippedGearRaw.speed_gear_id, equippedGearRaw.speed_gear_template_id, 'Shield');
      mapIdToMockNft(equippedGearRaw.stamina_gear_id, equippedGearRaw.stamina_gear_template_id, 'Boots');
      mapIdToMockNft(equippedGearRaw.xp_gear_id, equippedGearRaw.xp_gear_template_id, 'Book');
      mapIdToMockNft(equippedGearRaw.nft_gear_id, equippedGearRaw.nft_gear_template_id, 'Charm');
      mapIdToMockNft(equippedGearRaw.points_gear_id, equippedGearRaw.points_gear_template_id, 'Crest');
      mapIdToMockNft(equippedGearRaw.emblem_gear_id, equippedGearRaw.emblem_gear_template_id, 'Emblem');
    }

    // Then overwrite with Firebase data if present (newer sync)
    if (playerData?.equippedGear) {
      for (const [slot, gearInfo] of Object.entries(playerData.equippedGear)) {
        if (gearInfo && gearInfo.asset_id) {
          loadedGear[slot] = {
            asset_id: String(gearInfo.asset_id),
            template: { template_id: String(gearInfo.template_id) }
          };
        }
      }
    }

    // Finally, upgrade all mock NFTs in loadedGear to REAL NFTs if they exist in inventory
    if (nfts && nfts.length > 0) {
      for (const slot of Object.keys(loadedGear)) {
        const mockNft = loadedGear[slot];
        const actualNft = nfts.find(n => String(n.asset_id) === String(mockNft.asset_id));
        if (actualNft) {
          loadedGear[slot] = actualNft;
        } else {
          mockNft.name = `Item #${mockNft.asset_id}`;
        }
      }
    }

    setEquippedGear(loadedGear);
  }, [playerData?.equippedGear, equippedGearRaw, nfts]);

  // This hook runs EVERY TIME playerData changes to keep the quest state in sync.
  useEffect(() => {
    if (playerData) {
      setQuest(playerData.quest || null);
    }
  }, [playerData]);
  // ----------------------------------------------------------------------------
  // FIX 1: Partner Status (Gated by firebaseUser)
  // ----------------------------------------------------------------------------
  useEffect(() => {
    // CRITICAL: Wait for BOTH Session AND Firebase Auth
    if (!session || !firebaseUser) {
      setIsPartner(false);
      setVaultStakes([]);
      return;
    }

    const checkPartnerStatus = async () => {
      const waxIdFromSession = session.actor.toString();
      try {
        const partnerDocRef = doc(db, "partners", waxIdFromSession);
        const partnerDocSnap = await getDoc(partnerDocRef);
        if (partnerDocSnap.exists() && partnerDocSnap.data().status === 'active') {
          setIsPartner(true);
        } else {
          setIsPartner(false);
        }
      } catch (e) {
        console.warn("Partner check failed (likely auth delay):", e.message);
        setIsPartner(false);
      }
    };
    checkPartnerStatus();

    // Fetch on-chain stakes when session is ready
    fetchOnChainStakes(session.actor.toString());
  }, [session, firebaseUser]);

  const fetchWithRotation = async (body) => {
    for (const endpoint of WAX_RPC_ENDPOINTS) {
      try {
        const response = await fetch(`${endpoint}/v1/chain/get_table_rows`, {
          method: "POST",
          headers: { "Content-Type": "text/plain" },
          body: JSON.stringify(body)
        });

        if (response.ok) {
          return await response.json();
        } else {
          const errorText = await response.text();
          console.warn(`Node ${endpoint} rejected query:`, errorText);
        }
      } catch (err) {
        console.warn(`Node ${endpoint} connection failed:`, err.message);
      }
    }
    throw new Error("All WAX RPC nodes failed to respond.");
  };

  // Work out which season the player can actually claim, and what it is worth.
  //
  // The claim panel used to be a bare number box defaulting to 1, so a player
  // who did not know to change it hit a settled season and read the rejection
  // as the claim being broken. The chain already knows the answer: the most
  // recent closed season that still has a pool.
  const fetchSeasonClaim = useCallback(async (activeSession) => {
    try {
      const seasonRes = await fetchWithRotation({
        json: true, code: "whatifdragon", scope: "whatifdragon",
        table: "seasonstates", limit: 100
      });
      const rows = seasonRes.rows || [];
      const claimable = rows
        .filter((r) => !r.is_active && parseFloat(r.token_pool) > 0 && Number(r.total_monthly_points) > 0)
        .sort((a, b) => Number(b.season_id) - Number(a.season_id))[0];

      if (!claimable) {
        const open = rows.find((r) => r.is_active);
        setSeasonClaim({ status: "none", openSeasonId: open ? Number(open.season_id) : null });
        return;
      }

      const seasonId = Number(claimable.season_id);
      const pool = parseFloat(claimable.token_pool);
      const totalPoints = Number(claimable.total_monthly_points);
      // token_pool is only the 90% fair share that claims pay out of; the
      // other 10% went to the lottery winners at rollover. Show players the
      // whole season pool, but keep computing shares against token_pool.
      const totalPool = pool / 0.9;
      const info = { status: "open", seasonId, pool, totalPool, totalPoints,
                     symbol: claimable.token_pool.split(" ")[1] || "FIREMAN" };

      const actor = activeSession && activeSession.actor && activeSession.actor.toString();
      if (actor) {
        // seasonpoints/seasonclaims are keyed by the account's uint64 name
        // value, so bound the query by the name itself rather than scanning.
        const bound = { key_type: "name", lower_bound: actor, upper_bound: actor, limit: 1 };
        const [ptsRes, claimRes] = await Promise.all([
          fetchWithRotation({ json: true, code: "whatifdragon", scope: String(seasonId),
                              table: "seasonpoints", ...bound }),
          fetchWithRotation({ json: true, code: "whatifdragon", scope: String(seasonId),
                              table: "seasonclaims", ...bound })
        ]);
        info.myPoints = ptsRes.rows && ptsRes.rows.length ? Number(ptsRes.rows[0].points) : 0;
        info.alreadyClaimed = !!(claimRes.rows && claimRes.rows.length);
        info.myShare = totalPoints > 0 ? (info.myPoints * pool) / totalPoints : 0;
      }
      setSeasonClaim(info);
    } catch (err) {
      console.warn("Could not read season claim state:", err.message);
      setSeasonClaim({ status: "error" });
    }
  }, []);

  useEffect(() => {
    fetchSeasonClaim(session);
  }, [session, fetchSeasonClaim]);

  const fetchStakedAttackers = async (walletAddress) => {
    let allStakes = [];
    let more = true;
    let lowerBound = walletAddress;

    while (more) {
      try {
        const data = await fetchWithRotation({
          json: true,
          code: "whatifdragon",
          scope: "whatifdragon",
          table: "stakes", // Assuming generic stakes table, update if different
          index_position: "2",
          key_type: "name",
          lower_bound: lowerBound,
          upper_bound: walletAddress,
          limit: 500
        });

        const rows = data.rows || [];
        const userRows = rows.filter(r => !r.staker || r.staker === walletAddress);
        allStakes = allStakes.concat(userRows);

        more = data.more === true && rows.length > 0 && !!data.next_key;
        if (more) lowerBound = data.next_key;
      } catch (e) {
        console.warn("Failed to fetch staked attackers from whatifdragon:", e);
        break;
      }
    }

    // Convert to standard NFT objects if possible, or just return raw rows
    return allStakes;
  };

  // --- UPDATED: BULLETPROOF FETCH STAKES (RPC ROTATION) ---
  const fetchOnChainStakes = async (walletAddress) => {
    try {
      // Fetch staked attackers
      const attackersData = await fetchStakedAttackers(walletAddress);
      setStakedAttackers(attackersData);

      // 1. Fetch user's stakes (using secondary index byuser)
      // Limit 1000: fmtsm.wam has 325+ stakes - must fit in one page to avoid
      // broken secondary-index pagination (next_key is uint64, not a name string).
      let allRows = [];
      let more = true;
      let lowerBound = walletAddress;

      while (more) {
        const data = await fetchWithRotation({
          json: true,
          code: "whatifvault1",
          scope: "whatifvault1",
          table: "stakes",
          index_position: "2",
          key_type: "name",
          lower_bound: lowerBound,
          upper_bound: walletAddress,
          limit: 500
        });

        const rows = data.rows || [];
        // Filter to only this user's rows (field confirmed as 'staker' from contract data)
        const userRows = rows.filter(r => !r.staker || r.staker === walletAddress);
        allRows = allRows.concat(userRows);
        // Only paginate if node signals more AND provides a next_key
        more = data.more === true && rows.length > 0 && !!data.next_key;
        if (more) lowerBound = data.next_key;
      }

      // 2. Fetch User Boosts
      let fetchedBoosts = { gang_boost_scaled: 0, burn_boost_expires: 0 };
      try {
        const boostsData = await fetchWithRotation({
          json: true,
          code: "whatifvault1",
          scope: "whatifvault1",
          table: "userboosts",
          key_type: "name", // <--- THE FIX: Forces the node to parse fmtsm.wam correctly
          lower_bound: walletAddress,
          upper_bound: walletAddress,
          limit: 1
        });
        if (boostsData.rows && boostsData.rows.length > 0) {
          const row = boostsData.rows[0];
          fetchedBoosts.gang_boost_scaled = row.gang_boost_scaled || 0;
          fetchedBoosts.burn_boost_expires = new Date(row.burn_boost_expires + "Z").getTime();
        }
      } catch (e) {
        console.warn("Failed to fetch user boosts:", e);
      }
      setVaultUserBoosts(fetchedBoosts);

      // 4. Fetch On-Chain Equipment
      try {
        const equipData = await fetchWithRotation({
          json: true,
          code: "whatifdragon",
          scope: "whatifdragon",
          table: "equipment",
          key_type: "name",
          lower_bound: walletAddress,
          upper_bound: walletAddress,
          limit: 1
        });
        let equipRow = {};
        if (equipData.rows && equipData.rows.length > 0 && equipData.rows[0].player === walletAddress) {
          equipRow = equipData.rows[0];
        }

        // Also fetch from the separate emblems table
        try {
          const emblemData = await fetchWithRotation({
            json: true,
            code: "whatifdragon",
            scope: "whatifdragon",
            table: "emblems",
            key_type: "name",
            lower_bound: walletAddress,
            upper_bound: walletAddress,
            limit: 1
          });
          if (emblemData.rows && emblemData.rows.length > 0 && emblemData.rows[0].player === walletAddress) {
            const embRow = emblemData.rows[0];
            // Merge emblem as an 'Emblem' slot in the same gear object
            equipRow.emblem_gear_id = embRow.emblem_id;
            equipRow.emblem_gear_template_id = embRow.emblem_template_id;
          }
        } catch (embErr) {
          console.warn("Failed to fetch emblems table:", embErr);
        }

        setEquippedGearRaw(Object.keys(equipRow).length > 0 ? equipRow : {});
      } catch (e) {
        console.warn("Failed to fetch equipment:", e);
        setEquippedGearRaw({});
      }

      // 5. Fetch Playerstats2 (Base MP from Badges)
      try {
        const statsData = await fetchWithRotation({
          json: true,
          code: "whatifdragon",
          scope: "whatifdragon",
          table: "playerstats2",
          key_type: "name",
          lower_bound: walletAddress,
          upper_bound: walletAddress,
          limit: 1
        });
        if (statsData.rows && statsData.rows.length > 0 && statsData.rows[0].user === walletAddress) {
          setOnChainPlayerStats(statsData.rows[0]);
        }
      } catch (e) {
        console.warn("Failed to fetch playerstats2:", e);
      }

      // 5. Fetch Pool State (TVL)
      let tvlMap = {};
      try {
        const poolData = await fetchWithRotation({
          json: true,
          code: "whatifvault1",
          scope: "whatifvault1",
          table: "poolstate",
          limit: 100
        });
        if (poolData.rows) {
          poolData.rows.forEach(row => {
            if (row.total_staked) {
              const parts = row.total_staked.split(" ");
              tvlMap[parts[1]] = parseFloat(parts[0]);
            }
          });
        }
      } catch (e) {
        console.warn("Failed to fetch pool state:", e);
      }
      setVaultTotalValueLocked(tvlMap);

      const currentTime = Date.now();

      // Map lock_tier string -> numeric months for display
      const tierToMonths = {
        'flexible': 0.5,
        '1_month': 1,
        '3_months': 3,
        '6_months': 6,
        '12_months': 12
      };

      const formattedStakes = allRows.reduce((acc, row) => {
        try {
          // Guard against rows with missing/malformed staked_amount
          if (!row.staked_amount || typeof row.staked_amount !== 'string') {
            console.warn(`[Vault] Skipping malformed stake row (id: ${row.id}):`, row);
            return acc;
          }
          const parts = row.staked_amount.split(" ");
          const tokenSymbol = parts[1];
          const stakedAmount = parseFloat(parts[0]);

          if (!tokenSymbol || isNaN(stakedAmount)) {
            console.warn(`[Vault] Skipping stake with unparseable amount (id: ${row.id}):`, row.staked_amount);
            return acc;
          }

          const stakeObjForMath = {
            stakedAmount: stakedAmount,
            apr: row.base_apr,
            lastRewardCalculationTime: new Date((row.last_claim || row.staked_at) + "Z").getTime(),
            accruedRewards: 0
          };

          const rewardMath = calculateStakeRewardsPure(stakeObjForMath, currentTime);

          acc.push({
            id: row.id,
            onChainId: row.id,
            tokenSymbol: tokenSymbol,
            stakedAmount: stakedAmount,
            apr: row.base_apr ?? 0,
            lockTier: row.lock_tier || 'flexible',
            lockPeriodMonths: tierToMonths[row.lock_tier] ?? 0.5,
            stakedAt: { toDate: () => new Date((row.staked_at || row.last_claim) + "Z") },
            unlockTime: { toDate: () => new Date((row.unlock_time || row.staked_at) + "Z") },
            lastRewardCalculationTime: { toDate: () => new Date((row.last_claim || row.staked_at) + "Z") },
            stakeStatus: "active",
            accruedRewards: rewardMath.totalRewards
          });
        } catch (rowErr) {
          console.warn(`[Vault] Failed to parse stake row (id: ${row?.id}), skipping:`, rowErr.message);
        }
        return acc;
      }, []);

      setVaultStakes(formattedStakes);
    } catch (error) {
      console.error("Failed to read blockchain table:", error);
    }
  };

  const handleSessionLogic = useCallback(async (session) => {
    try {
      console.log(`Restoring game session for ${session.actor}...`);

      setSession(session);
      if (auth.currentUser) {
        try {

        } catch (e) {
          console.warn("Daily reward check skipped:", e.message);
        }
      }
    } catch (error) {
      console.error("Error during session setup:", error);
    }
  }, []);

  const [stakedAttackers, setStakedAttackers] = useState([]);
  const [firebaseStakes, setFirebaseStakes] = useState([]);
  const verifyWaxLogin = async (session) => {
    if (!session) return;

    try {
      const walletAddress = session.actor.toString();
      const timestamp = Date.now();
      const message = `Login to WHATIF Dungeon For Riches: ${timestamp}`;
      let signature;
      const provider = session.walletPlugin.signatureProvider;

      console.log("Please implement the specific wallet signing call here based on your installed plugins.");
      const verifyWaxLogin = httpsCallable(functions, 'verifyWaxLogin');

      const result = await verifyWaxLogin({
        wallet: walletAddress,
        signature: signature, // The signature string obtained above
        message: message      // The exact string with timestamp
      });

      if (result.data.token) {
        await signInWithCustomToken(auth, result.data.token);
        console.log("Firebase Login Successful!");
      }

    } catch (error) {
      console.error("Login failed:", error);
      alert(`Login Failed: ${error.message}`);
    }
  };

  const handleSelectChange = async (event, schema) => {
    if (playerData?.isBlacklisted) {
      alert("You are blacklisted and cannot change equipped NFTs.");
      return;
    }

    const selectedId = event.target.value;
    const slot = event.target.name;

    try {
      const assetId = selectedId ? selectedId : null;

      if (schema === 'gear') {
        if (!session) throw new Error("Wallet not connected.");

        const slotMapping = {
          'Sword': 'attack',
          'Shield': 'speed',
          'Boots': 'stamina',
          'Book': 'xp',
          'Charm': 'nft',
          'Crest': 'points',
          'Emblem': 'emblem'
        };
        const contractSlot = slotMapping[slot] || slot.toLowerCase();

        if (assetId) {
          // Equipping
          const action = {
            account: "atomicassets",
            name: "transfer",
            authorization: [session.permissionLevel],
            data: {
              from: session.actor,
              to: "whatifdragon",
              asset_ids: [assetId],
              memo: `equip:${contractSlot}`
            }
          };
          await session.transact({ actions: [action] });
        } else {
          // Unequipping
          const action = {
            account: "whatifdragon",
            name: "unequip",
            authorization: [session.permissionLevel],
            data: {
              owner: session.actor,
              slot: contractSlot
            }
          };
          await session.transact({ actions: [action] });
        }

        // Refresh local data to show changes
        await fetchOnChainStakes(session.actor.toString());
        await fetchNfts(session.actor.toString());
      } else {
        await equipItemCallable({
          assetId: assetId,
          itemType: schema, // 'deeds'
          slot: slot
        });
      }
    } catch (error) {
      console.error("Error equipping item:", error);
      alert(`Failed to equip item: ${error.message}`);
    }
  };
  // Removed heal and max heal logic

  const checkPlayerStatus = useCallback(async () => {
    if (!adminTargetPlayer) {
      setAdminTargetPlayerStatus(null);
      return;
    }
    setAdminLoadingStatus(true);
    setAdminTargetPlayerStatus(null);
    try {
      const playerDocRef = doc(db, "players", adminTargetPlayer);
      const playerDocSnap = await getDoc(playerDocRef);
      if (playerDocSnap.exists()) {
        const data = playerDocSnap.data();
        setAdminTargetPlayerStatus(data.isBlacklisted ? 'Blacklisted' : 'Not Blacklisted');
      } else {
        setAdminTargetPlayerStatus('Not Found');
      }
    } catch (error) {
      console.error("Error checking player status:", error);
      setAdminTargetPlayerStatus('Error checking status.');
    } finally {
      setAdminLoadingStatus(false);
    }
  }, [adminTargetPlayer]);
  const handleBlacklist = useCallback(async () => {
    if (!adminTargetPlayer) return;
    setAdminLoadingStatus(true);
    try {
      const result = await blacklistPlayerCallable({ targetPlayerId: adminTargetPlayer });
      alert(result.data.message);
      await checkPlayerStatus();
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setAdminLoadingStatus(false);
    }
  }, [adminTargetPlayer, blacklistPlayerCallable, checkPlayerStatus]);
  const handleUnblacklist = useCallback(async () => {
    if (!adminTargetPlayer) return;
    setAdminLoadingStatus(true);
    try {
      const result = await unblacklistPlayerCallable({ targetPlayerId: adminTargetPlayer });
      alert(result.data.message);
      await checkPlayerStatus();
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setAdminLoadingStatus(false);
    }
  }, [adminTargetPlayer, unblacklistPlayerCallable, checkPlayerStatus]);

  const handleManualSendNft = useCallback(async () => {
    if (!sendNftTarget || !sendAssetId || !sendNftMemo) {
      setSendNftFeedback('Recipient, Asset ID, and Memo are all required.');
      return;
    }
    setIsSendingNft(true);
    setSendNftFeedback('Sending NFT...');
    try {
      const result = await adminSendNftCallable({
        targetPlayerId: sendNftTarget,
        assetId: sendAssetId,
        memo: sendNftMemo
      });
      setSendNftFeedback(result.data.message);
    } catch (error) {
      console.error("Error sending NFT:", error);
      setSendNftFeedback(`Error: ${error.message}`);
    } finally {
      setIsSendingNft(false);
    }
  }, [sendNftTarget, sendAssetId, sendNftMemo, adminSendNftCallable]);
  const handleManualSendToken = useCallback(async () => {
    if (!sendTokenTarget || !sendTokenAmount || !sendTokenSymbol || !sendTokenMemo) {
      setSendTokenFeedback('Recipient, Amount, Token Symbol, and Memo are all required.');
      return;
    }
    const parsedAmount = parseFloat(sendTokenAmount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setSendTokenFeedback('Amount must be a positive number.');
      return;
    }
    setIsSendingToken(true);
    setSendTokenFeedback('Sending Token...');
    try {
      const result = await adminSendTokenCallable({
        targetPlayerId: sendTokenTarget,
        amount: parsedAmount,
        tokenSymbol: sendTokenSymbol,
        memo: sendTokenMemo,
      });
      setSendTokenFeedback(result.data.message);
    } catch (error) {
      console.error("Error sending token:", error);
      setSendTokenFeedback(`Error: ${error.message}`);
    } finally {
      setIsSendingToken(false);
    }
  }, [sendTokenTarget, sendTokenAmount, sendTokenSymbol, sendTokenMemo, adminSendTokenCallable]);
  const handleAirdropFromPast = useCallback(async () => {
    if (!airdropDate) {
      setAirdropFeedback('Error: A Target Date is required.');
      return;
    }
    if (!window.confirm(`Are you SURE you want to run airdrop for ${airdropDate}? This is irreversible.`)) {
      return;
    }
    setIsAirdropping(true);
    setAirdropFeedback(`Processing airdrop for ${airdropDate}...`);
    try {
      const assetIdsArray = airdropAssetIds.split(/[,\n]/).map(id => id.trim()).filter(id => id);
      const result = await adminAirdropFromPastLeaderboardCallable({
        dateString: airdropDate,
        assetIds: assetIdsArray, // Send the array, even if it's empty
      });
      setAirdropFeedback(result.data.message);
    } catch (error) {
      console.error("Error running past airdrop:", error);
      setAirdropFeedback(`Error: ${error.message}`);
    } finally {
      setIsAirdropping(false);
    }
  }, [airdropDate, airdropAssetIds, adminAirdropFromPastLeaderboardCallable]);
  const handleResetAllWeeklyPoints = useCallback(async () => {
    if (!window.confirm("DANGER: This will reset weekly points for ALL players to zero. This action cannot be undone. Are you absolutely sure?")) {
      return;
    }
    setIsResettingAllWeekly(true);
    setResetAllWeeklyFeedback('Initiating global reset of all weekly points...');
    try {
      const result = await adminResetAllWeeklyPointsCallable();
      setResetAllWeeklyFeedback(result.data.message);
    } catch (error) {
      console.error("Error resetting all weekly points:", error);
      setResetAllWeeklyFeedback(`Error: ${error.message}`);
    } finally {
      setIsResettingAllWeekly(false);
    }
  }, [adminResetAllWeeklyPointsCallable]);
  // ==================================================================
  // VAULT ACTIONS: Now send transactions directly to the whatifvault1
  // smart contract. After each success, we call syncPlayerVaultState
  // so Firebase updates vaultScore / totalBoost instantly for combat.
  // ==================================================================

  const handleInitiateVaultStake = async () => {
    if (!session || !playerData || playerData?.isBlacklisted) {
      setVaultStakingFeedback("You are blacklisted or not logged in.");
      return;
    }
    const tokenSymbol = selectedVaultToken;
    const amount = parseFloat(vaultStakeAmountInput);
    const { min_stake, decimals } = VAULT_TOKEN_INFO[tokenSymbol];
    if (isNaN(amount) || amount < min_stake) {
      setVaultStakingFeedback(`Minimum stake is ${min_stake} ${tokenSymbol}.`);
      return;
    }
    setIsInitiatingVaultStake(true);
    setVaultStakingFeedback('Step 1 of 3: Sending stake transfer to the vault contract...');
    try {
      const tokenContract = tokenContracts[tokenSymbol];
      const quantity = `${amount.toFixed(decimals)} ${tokenSymbol}`;
      const memo = `STAKE:${vaultLockPeriodInput}`;

      // Send a standard token transfer to whatifvault1 with memo STAKE:<lockPeriod>
      const actions = [{
        account: tokenContract,
        name: 'transfer',
        authorization: [session.permissionLevel],
        data: {
          from: session.actor.toString(),
          to: 'whatifvault1',
          quantity: quantity,
          memo: memo,
        },
      }];

      setVaultStakingFeedback('Step 2 of 3: Please approve the transfer in your wallet...');
      await session.transact({ actions });

      setVaultStakingFeedback('Step 3 of 3: Synchronizing vault state with game...');
      // Brief wait to allow the chain to propagate, then sync Firebase
      await new Promise(resolve => setTimeout(resolve, 3000));
      await syncPlayerVaultStateCallable();

      // Refresh on-chain data and token balances
      await fetchOnChainStakes(session.actor.toString());
      await fetchTokenBalances(session);
      setVaultStakeAmountInput('');
      setVaultStakingFeedback(`✅ Success! ${quantity} staked in the Vault. Your boosts are now active.`);
    } catch (error) {
      console.error('Error staking:', error);
      setVaultStakingFeedback(`❌ Error: ${error.message}`);
    } finally {
      setIsInitiatingVaultStake(false);
    }
  };

  const handleClaimVaultRewards = async (stakeId) => {
    if (playerData?.isBlacklisted) {
      setVaultStakingFeedback("You are blacklisted and cannot claim rewards.");
      return;
    }
    if (!session || !playerData) {
      setVaultStakingFeedback("Please log in first.");
      return;
    }
    const stake = vaultStakes.find(s => s.id === stakeId);
    if (!stake) return;

    setVaultStakingFeedback('Sending claim transaction to the vault contract...');
    try {
      // PARAMETER FIXED: Changed 'owner' to 'user' to align perfectly with the smart contract
      const actions = [{
        account: 'whatifvault1',
        name: 'claim',
        authorization: [session.permissionLevel],
        data: {
          user: session.actor.toString(),
          stake_id: stake.onChainId ?? stakeId,
        },
      }];

      await session.transact({ actions });

      setVaultStakingFeedback('Synchronizing vault state with game...');
      await new Promise(resolve => setTimeout(resolve, 3000));
      await syncPlayerVaultStateCallable();

      await fetchOnChainStakes(session.actor.toString());
      await fetchTokenBalances(session);
      setVaultStakingFeedback('✅ Rewards claimed successfully!');
    } catch (error) {
      console.error("Error claiming rewards:", error);
      if (error.message && error.message.includes("Reward pool")) {
        setVaultStakingFeedback("⏳ The reward pool is currently syncing. Don't worry, your principal is 100% safe! Please wait for the Admin to top up the Vault.");
      } else {
        setVaultStakingFeedback(`❌ Error claiming: ${error.message}`);
      }
    }
  };

  // --- CLAIM ALL REWARDS: chunked to avoid WAX CPU rejection on large wallets ---
  const handleClaimAllVaultRewards = async () => {
    if (playerData?.isBlacklisted) {
      setVaultStakingFeedback("You are blacklisted and cannot claim rewards.");
      return;
    }
    if (!session || !playerData) {
      setVaultStakingFeedback("Please log in first.");
      return;
    }

    // Filter out stakes that have no rewards to save on CPU
    const stakesToClaim = vaultStakes.filter(s => s.accruedRewards > 0.00001);

    if (stakesToClaim.length === 0) {
      setVaultStakingFeedback("No active rewards available to claim right now.");
      return;
    }

    const CHUNK_SIZE = 15;
    const chunks = [];
    for (let i = 0; i < stakesToClaim.length; i += CHUNK_SIZE) {
      chunks.push(stakesToClaim.slice(i, i + CHUNK_SIZE));
    }

    const totalChunks = chunks.length;
    setVaultStakingFeedback(`Preparing ${stakesToClaim.length} claim(s) across ${totalChunks} transaction(s)...`);

    try {
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        setVaultStakingFeedback(`⏳ Please sign transaction ${i + 1} of ${totalChunks} (${chunk.length} claims)...`);

        const actions = chunk.map(stake => ({
          account: 'whatifvault1',
          name: 'claim',
          authorization: [session.permissionLevel],
          data: {
            user: session.actor.toString(),
            stake_id: stake.onChainId ?? stake.id,
          },
        }));

        await session.transact({ actions });

        if (i < chunks.length - 1) {
          await new Promise(r => setTimeout(r, 1000));
        }
      }

      setVaultStakingFeedback('Synchronizing vault state with game...');

      const syncVaultState = httpsCallable(functions, 'syncPlayerVaultState');
      await new Promise(resolve => setTimeout(resolve, 3000));
      await syncVaultState();

      await fetchOnChainStakes(session.actor.toString());
      await fetchTokenBalances(session);
      setVaultStakingFeedback(`✅ Successfully claimed all rewards across ${stakesToClaim.length} stakes (${totalChunks} transaction${totalChunks > 1 ? 's' : ''})!`);
    } catch (error) {
      console.error("Error claiming all rewards:", error);
      if (error.message && error.message.includes("Reward pool")) {
        setVaultStakingFeedback("⏳ The reward pool is currently syncing. Your principal is 100% safe! Please wait for the Admin to top up the Vault.");
      } else {
        setVaultStakingFeedback(`❌ Error claiming all: ${error.message}`);
      }
    }
  };

  const handleClaimMonthlyReward = async (seasonId) => {
    if (playerData?.isBlacklisted) {
      alert("You are blacklisted and cannot claim rewards.");
      return;
    }
    if (!session || !playerData) {
      alert("Please log in first.");
      return;
    }

    try {
      const actions = [{
        account: 'whatifdragon',
        name: 'claimreward',
        authorization: [session.permissionLevel],
        data: {
          player: session.actor.toString(),
          season_id: parseInt(seasonId, 10),
        },
      }];

      await session.transact({ actions });

      alert('✅ Monthly reward claimed successfully!');
      await fetchTokenBalances(session);
      await fetchSeasonClaim(session);
    } catch (error) {
      console.error("Error claiming monthly reward:", error);
      alert(`❌ Error claiming reward: ${error.message}`);
    }
  };

  const handleUnstakeVaultTokens = async (stakeId) => {
    if (playerData?.isBlacklisted) {
      setVaultStakingFeedback("You are blacklisted and cannot unstake.");
      return;
    }
    if (!session || !playerData) {
      setVaultStakingFeedback("Please log in first.");
      return;
    }
    const stake = vaultStakes.find(s => s.id === stakeId);
    if (!stake || stake.stakedAmount <= 0) {
      setVaultStakingFeedback("You have no active stake to unstake.");
      return;
    }
    const unlockTimeMs = stake.unlockTime?.toMillis?.() ?? (stake.unlockTime?.toDate?.().getTime()) ?? 0;
    const nowMs = Date.now();
    if (unlockTimeMs && nowMs < unlockTimeMs) {
      const daysLeft = Math.ceil((unlockTimeMs - nowMs) / (1000 * 60 * 60 * 24));
      setVaultStakingFeedback(`This stake is locked for approximately ${daysLeft} more days.`);
      return;
    }

    setVaultStakingFeedback('Sending unstake transaction to the vault contract...');
    try {
      const actions = [{
        account: 'whatifvault1',
        name: 'unstake',
        authorization: [session.permissionLevel],
        data: {
          user: session.actor.toString(),
          stake_id: stake.onChainId ?? stakeId,
        },
      }];

      await session.transact({ actions });

      setVaultStakingFeedback('Synchronizing vault state with game...');
      await new Promise(resolve => setTimeout(resolve, 3000));
      await syncPlayerVaultStateCallable();

      await fetchOnChainStakes(session.actor.toString());
      await fetchTokenBalances(session);
      setVaultStakingFeedback('✅ Unstaked successfully! Tokens and rewards returned to your wallet.');
    } catch (error) {
      console.error("Error unstaking:", error);
      if (error.message && error.message.includes("Reward pool")) {
        setVaultStakingFeedback("⏳ The reward pool is currently syncing. Your principal is 100% safe! Please wait for the Admin to top up the Vault.");
      } else {
        setVaultStakingFeedback(`❌ Error unstaking: ${error.message}`);
      }
    }
  };

  // --- UNSTAKE ALL UNLOCKED: chunked sequential WAX transactions ---
  const handleUnstakeAll = async (tokenSymbol) => {
    if (playerData?.isBlacklisted) {
      setVaultStakingFeedback("You are blacklisted and cannot unstake.");
      return;
    }
    if (!session || !playerData) {
      setVaultStakingFeedback("Please log in first.");
      return;
    }

    const nowMs = Date.now();
    const unlockedStakes = vaultStakes.filter(s => {
      if (s.tokenSymbol !== tokenSymbol) return false;
      if (s.stakedAmount <= 0) return false;
      const unlockMs = s.unlockTime?.toMillis?.() ?? (s.unlockTime?.toDate?.().getTime()) ?? 0;
      return nowMs >= unlockMs;
    });

    if (unlockedStakes.length === 0) {
      setVaultStakingFeedback(`No unlocked ${tokenSymbol} stakes are ready to withdraw.`);
      return;
    }

    if (!window.confirm(`Unstake ALL ${unlockedStakes.length} unlocked ${tokenSymbol} position(s)? This will require multiple wallet signatures.`)) return;

    const CHUNK_SIZE = 15;
    const chunks = [];
    for (let i = 0; i < unlockedStakes.length; i += CHUNK_SIZE) {
      chunks.push(unlockedStakes.slice(i, i + CHUNK_SIZE));
    }

    const totalChunks = chunks.length;

    try {
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        setVaultStakingFeedback(`⏳ Please sign transaction ${i + 1} of ${totalChunks} (${chunk.length} unstakes)...`);

        const actions = chunk.map(stake => ({
          account: 'whatifvault1',
          name: 'unstake',
          authorization: [session.permissionLevel],
          data: {
            user: session.actor.toString(),
            stake_id: stake.onChainId ?? stake.id,
          },
        }));

        await session.transact({ actions });

        if (i < chunks.length - 1) {
          await new Promise(r => setTimeout(r, 1000));
        }
      }

      setVaultStakingFeedback('Synchronizing vault state with game...');
      await new Promise(resolve => setTimeout(resolve, 3000));
      await syncPlayerVaultStateCallable();
      await fetchOnChainStakes(session.actor.toString());
      await fetchTokenBalances(session);
      setVaultStakingFeedback(`✅ Unstaked all ${unlockedStakes.length} ${tokenSymbol} position(s) across ${totalChunks} transaction${totalChunks > 1 ? 's' : ''}!`);
    } catch (error) {
      console.error("Error in handleUnstakeAll:", error);
      if (error.message && error.message.includes("Reward pool")) {
        setVaultStakingFeedback("⏳ The reward pool is syncing. Your principal is safe! Please wait and retry.");
      } else {
        setVaultStakingFeedback(`❌ Unstake failed: ${error.message}`);
      }
    }
  };

  const handleBurnAprEmblem = async () => {
    if (!session) return;

    // Find the Watchtower Yield Emblem (Template: 870362)
    const aprEmblem = nfts.find(nft => nft.template.template_id === "870362");

    if (!aprEmblem) {
      setVaultStakingFeedback("❌ You do not own a Watchtower Yield Emblem.");
      return;
    }

    if (!window.confirm("This will permanently burn your Emblem to grant +0.25% APR on your Vault stakes for 24 hours. Continue?")) return;

    setVaultStakingFeedback('Sending NFT to the Vault contract to be burned...');

    try {
      const actions = [{
        account: 'atomicassets',
        name: 'transfer',
        authorization: [session.permissionLevel],
        data: {
          from: session.actor.toString(),
          to: 'whatifvault1',
          asset_ids: [aprEmblem.asset_id],
          memo: 'BURN_APR', // The exact memo the smart contract is listening for
        },
      }];

      await session.transact({ actions });

      setVaultStakingFeedback('✅ Emblem burned! +0.25% APR boost activated for 24 hours.');

      // Refresh local data to remove the burned NFT from the UI
      await fetchOnChainStakes(session.actor.toString());
      // Re-fetch NFTs to reflect the burned emblem
      await fetchNfts(session.actor.toString());
    } catch (error) {
      console.error('Error burning emblem:', error);
      setVaultStakingFeedback(`❌ Error: ${error.message}`);
    }
  };



  useEffect(() => {
    document.body.style.backgroundImage = `url(${backgroundImage})`;
    document.body.style.backgroundSize = "cover";
    document.body.style.backgroundAttachment = "fixed";
    document.body.style.color = "white";
    return () => {
      document.body.style.backgroundImage = "";
      document.body.style.backgroundSize = "";
      document.body.style.backgroundAttachment = "";
      document.body.style.color = "";
    };
  }, []);


  const renderPage = () => {
    // Real-token betting isn't allowed in a Google Play app without a gambling licence
    if (isNative && currentPage === 'hilo') return null;

    // 🛡️ Web3 / DeFi Gate for Native Mobile Builds (Compliance with Google Play Console Requirements)
    if (isNative && ['vault', 'staking', 'partner'].includes(currentPage)) {
      const featureLabels = {
        vault: 'Token Vault & APR Staking',
        staking: 'NFT Staking Rewards',
        partner: 'Partner Portal'
      };
      return <WebPortalGate featureName={featureLabels[currentPage] || currentPage} />;
    }

    const publicPages = ['store', 'leaderboard', 'about', 'vault', 'gangs', 'terms', 'privacy', 'hilo'];

    if (!session) {
      if (publicPages.includes(currentPage)) {
        switch (currentPage) {
          case 'store':
            return (
              <>
                <SEO title="Item Store" description="Buy Game Packs, Gear, and Gang Passes." path="store" />
                <div style={{ textAlign: 'center', marginBottom: '20px', padding: '10px', background: 'rgba(255, 165, 0, 0.2)', border: '1px solid orange', borderRadius: '5px' }}>
                  ⚠️ <b>Guest Mode:</b> You are viewing the store. Connect your wallet to purchase items.
                </div>
                <StorePage session={null} purchaseStoreItemCallable={null} refreshNfts={() => { }} functions={functions} />
              </>
            );
          case 'leaderboard':
            return (
              <>
                <SEO title="Leaderboard" description="View top players in the Daily, Weekly, and Vault leaderboards." path="leaderboard" />
                <LeaderboardPage 
                  playerData={null} 
                  leaderboardView={leaderboardView} 
                  setLeaderboardView={setLeaderboardView} 
                  handleClaimMonthlyReward={handleClaimMonthlyReward}
                  seasonClaim={seasonClaim}
                />
                {!isNative && (
                  <>
                    <LastDailyLeaderboardPage />
                    <LastSeasonalLeaderboardPage />
                  </>
                )}
              </>
            );
          case 'about':
            return <><SEO title="Game Guide" description="Learn the mechanics of WHATIF Battle For Riches." path="about" /><AboutUs /></>;
          case 'vault':
            return (
              <>
                <SEO title="Vault" description="Stake tokens to earn APR." path="vault" />
                <div style={{ textAlign: 'center', padding: '40px', background: 'rgba(0,0,0,0.7)', borderRadius: '15px', maxWidth: '800px', margin: 'auto' }}>
                  <h2>WHATIF Vault</h2>
                  <p>Stake your tokens to earn APR and Game Boosts.</p>
                  <p><i>Connect your wallet to manage your stakes.</i></p>
                  <h3 style={{ marginTop: '30px' }}>Top Stakers</h3>
                  <LeaderboardPage playerData={null} leaderboardView='vault' setLeaderboardView={() => { }} />
                </div>
              </>
            );
          case 'gangs':
            return (
              <>
                <SEO title="Gangs" description="Join forces with other players." path="gangs" />
                <div style={{ textAlign: 'center', padding: '40px', background: 'rgba(0,0,0,0.7)', borderRadius: '15px', maxWidth: '800px', margin: 'auto' }}>
                  <h2>Gangs of WAX</h2>
                  <p>Join a gang to pool resources and unlock powerful building buffs.</p>
                  <p><i>Connect your wallet to create or join a gang.</i></p>
                </div>
              </>
            );
          case 'hilo':
            return (
              <>
                <SEO title="Hi-Lo Jackpot" description="Play Hi-Lo." path="hilo" />
                <div style={{ textAlign: 'center', marginBottom: '20px', padding: '10px', background: 'rgba(0, 229, 255, 0.15)', border: '1px solid #00e5ff', borderRadius: '5px' }}>
                  ℹ️ <b>Guest Preview:</b> Sign in or connect wallet to place bets and claim the faucet.
                </div>
                <HiLoJackpotPage session={null} playerData={playerData} claimFaucetCallable={claimFaucetCallable} playHiLoCallable={playHiLoCallable} playCrashCallable={playCrashCallable} cashOutCrashCallable={cashOutCrashCallable} crashStatusCallable={crashStatusCallable} requestWithdrawalCallable={requestWithdrawalCallable} verifyDepositCallable={verifyDepositCallable} fetchTokenBalances={fetchTokenBalances} />
              </>
            );
          case 'terms':
            return <><SEO title="Terms of Service" description="Legal Terms." path="terms" /><TermsPage /></>;
          case 'privacy':
            return <><SEO title="Privacy Policy" description="What we collect and how to delete it." path="privacy" /><PrivacyPage /></>;
          default:
            return null;
        }
      } else {
        return (
          <div style={{ textAlign: 'center', marginTop: '50px', padding: '40px', background: 'rgba(0,0,0,0.5)', borderRadius: '15px', maxWidth: '600px', margin: '50px auto' }}>
            <h2>Restricted Access</h2>
            <p>
              {isNative
                ? `Please sign in to access ${currentPage.charAt(0).toUpperCase() + currentPage.slice(1)}.`
                : `You must connect your WAX Wallet or sign in to access the ${currentPage.charAt(0).toUpperCase() + currentPage.slice(1)}.`}
            </p>
            {pendingSession ? (
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => verifyIdentity(pendingSession)}
                  disabled={loading}
                  className="modern-button"
                  style={{
                    boxShadow: '0 0 15px #00e5ff',
                    border: '1px solid #00e5ff',
                    background: 'linear-gradient(145deg, #007bff, #0056b3)',
                    padding: '12px 24px',
                    fontSize: '16px'
                  }}
                >
                  {loading ? "Verifying..." : `🔐 VERIFY IDENTITY (${String(pendingSession.actor)})`}
                </button>
                <button
                  onClick={async () => {
                    setPendingSession(null);
                    clearGoogleSession();
                    if (sessionKit) await sessionKit.logout().catch(() => {});
                  }}
                  className="modern-button"
                  style={{
                    boxShadow: '0 0 10px rgba(255, 68, 68, 0.4)',
                    border: '1px solid #ff4444',
                    background: 'rgba(255, 68, 68, 0.2)',
                    color: '#ff8888',
                    padding: '12px 20px',
                    fontSize: '14px'
                  }}
                >
                  ✕ Cancel / Switch
                </button>
              </div>
            ) : (
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={handleGoogleLogin}
                  className="modern-button"
                  style={{
                    boxShadow: '0 0 10px #4285F4',
                    border: '1px solid #4285F4',
                    background: 'linear-gradient(135deg, #4285F4 0%, #1a73e8 100%)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24">
                    <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                    <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                  </svg>
                  Sign In with Google
                </button>
                <button onClick={handleLogin} disabled={loading} className="modern-button" style={{ boxShadow: '0 0 10px #ff9800', border: '1px solid #ff9800' }}>
                  {loading ? "Connecting..." : "🔌 Connect Wallet"}
                </button>
                <button
                  id="reviewer-login-cta-btn"
                  onClick={() => setShowReviewerModal(true)}
                  className="modern-button"
                  style={{ background: 'linear-gradient(135deg, #00c6ff, #0072ff)', border: 'none', color: '#fff', fontWeight: 'bold' }}
                >
                  🔑 Sign In as Reviewer
                </button>
              </div>
            )}
          </div>
        );
      }
    }
    if (!playerData) {
      return <div style={{ textAlign: 'center', marginTop: '50px', color: '#ff9800', animation: 'pulse 1s infinite' }}>Loading Player Profile...</div>;
    }

    const totalRevivesAvailable = emblemNfts.reduce((total, nft) => total + (itemTemplates[nft.template.template_id]?.revives || 0), 0);
    const revivesLeft = totalRevivesAvailable - (playerData.revivesUsedThisWeek || 0);

    const totalBoosts = { 'MP Boost': 0, 'Speed Boost': 0, 'Stamina Boost': 0, 'Base MP Boost': 0, 'Points Gain': 0, 'XP Gain': 0, 'NFT Gain Chance': 0, 'Vault APR Boost': 0 };

    const boostSources = [activeBoosts, playerData.nftStakeBoosts, gangData?.gangBoosts];
    for (const source of boostSources) {
      if (source) {
        for (const [boostType, value] of Object.entries(source)) {
          if (totalBoosts.hasOwnProperty(boostType)) totalBoosts[boostType] += value;
        }
      }
    }
    if (playerData.partnerMPBoost) totalBoosts['Base MP Boost'] += playerData.partnerMPBoost;
    if (playerData.dailyBoost) { const { type, value } = playerData.dailyBoost; if (totalBoosts.hasOwnProperty(type)) totalBoosts[type] += value; }
    if (playerData.activeReviveBoost) { const { type, value } = playerData.activeReviveBoost; if (totalBoosts.hasOwnProperty(type)) totalBoosts[type] += value; }
    if (playerData.vault_summary?.totalBoost) {
      totalBoosts['MP Boost'] += playerData.vault_summary.totalBoost;
    }

    // Add On-Chain NFT Staking Boosts
    if (stakedAttackers && stakedAttackers.length > 0) {
      stakedAttackers.forEach(stake => {
        if (stake.boost_provided) {
          totalBoosts['MP Boost'] += (parseInt(stake.boost_provided) || 0);
        }
      });
    }
    // Add Daily Burn Emblem Boosts
    const nowTime = Date.now();
    if (playerData.activeBoosts) {
      if (playerData.activeBoosts.boost_atk > nowTime) totalBoosts['MP Boost'] += 10;
      if (playerData.activeBoosts.boost_def > nowTime) totalBoosts['Speed Boost'] += 10;
      if (playerData.activeBoosts.boost_pts > nowTime) totalBoosts['Points Gain'] += 10;
      if (playerData.activeBoosts.boost_xp > nowTime) totalBoosts['XP Gain'] += 10;
      if (playerData.activeBoosts.boost_nft > nowTime) totalBoosts['NFT Gain Chance'] += 10;
    }

    const effectiveBoosts = {};
    for (const [boostType, rawValue] of Object.entries(totalBoosts)) {
      effectiveBoosts[boostType] = calculateDiminishedBoost(rawValue);
    }

    const xpPercentage = (playerData.xp / calculateXpForNextLevel(playerData.level)) * 100;


    const rawStaminaBoost = totalBoosts['Stamina Boost']; // Already summed above
    const effectiveStaminaBoost = effectiveBoosts['Stamina Boost'];
    const burnStamina = (playerData.activeBoosts?.boost_stam_max > nowTime) ? 10 : 0;
    const maxStamina = Math.floor(BASE_MAX_STAMINA * (1 + effectiveStaminaBoost / 100)) + burnStamina;
    const currentStaminaVal = playerData.stamina !== undefined ? playerData.stamina : (maxStamina - (playerData.dailyStaminaUsed || 0));

    let baseMP = calculateBaseMP(playerData.level);

    // 1. Add MP from equipped Emblem (Revives)
    if (playerData.equippedGear?.['Emblem']) {
      const emblemTemplate = itemTemplates[playerData.equippedGear['Emblem'].template_id];
      if (emblemTemplate?.mp) {
        baseMP += emblemTemplate.mp;
      }
    }

    // 2. Add flat MP from Firebase staked NFTs (if any)
    // Badge Boosts (From On-Chain Player Stats)
    baseMP += (onChainPlayerStats?.total_mp || 0);

    // Flat MP Boost (from items/purchases)
    if (playerData.flatMpBoost) {
      baseMP += playerData.flatMpBoost;
    }

    // 2b. Add passive MP from held Soulbound Keycards (WHATIF Verified Master Keycard #909865)
    // Non-transferable/soulbound NFTs cannot be staked/transferred to whatifdragon; MP is credited directly from wallet inventory.
    const uniqueMasterKeycards = new Map();
    (nfts || []).forEach(nft => {
      const tid = String(nft.template?.template_id || nft.template_id || '');
      if (tid === '909865') {
        const key = nft.asset_id ? String(nft.asset_id) : `idx_${uniqueMasterKeycards.size}`;
        uniqueMasterKeycards.set(key, nft);
      }
    });
    if (uniqueMasterKeycards.size > 0) {
      baseMP += (uniqueMasterKeycards.size * 5000);
    }

    // Add On-Chain NFT Staking MP
    if (stakedAttackers && stakedAttackers.length > 0) {
      stakedAttackers.forEach(stake => {
        if (stake.mp_provided) {
          baseMP += (parseInt(stake.mp_provided) || 0);
        }
      });
    }

    // 3. Final MP includes MP Boost AND Base MP Boost (from Gangs/NFTs)
    const combinedAttackMultiplier = (effectiveBoosts['MP Boost'] || 0) + (effectiveBoosts['Base MP Boost'] || 0);
    let totalMP = Math.floor(baseMP * (1 + combinedAttackMultiplier / 100));

    let finalDeed = null; // Deprecated

    const gearSlots = ['Crest', 'Book', 'Charm', 'Boots', 'Shield', 'Sword', 'Emblem'];

    switch (currentPage) {
      case 'unbox':
        return (
          <>
            <SEO title="Unboxing Station" description="Open your WHATIF packs." path="unbox" />
            <UnboxPage
              nfts={nfts}
              session={session}
              refreshNfts={() => { if (session) { fetchNfts(session.actor.toString()); fetchOnChainStakes(session.actor.toString()); } }}
            />
          </>
        );
      case 'profile':
        return (
          <div style={{ backgroundColor: 'rgba(0,0,0,0.7)', padding: '20px', borderRadius: '8px', maxWidth: '800px', margin: 'auto' }}>
            <SEO title="Player Profile" description="Manage your stats, equip NFTs, and prepare for your Dungeon!" path="profile" />
            <h2 style={{ textAlign: 'center' }}>Player Profile</h2>

            <CharacterDisplay
              profile={playerData}
              walletAddress={session?.actor.toString()}
              activeEmblem={playerData?.equippedGear?.Emblem}
              activeGear={playerData?.equippedGear}
              tokenBalances={tokenBalances}
              gangBoosts={gangData?.gangBoosts}
              vaultUserBoosts={vaultUserBoosts}
              syncPlayerVaultStateCallable={syncPlayerVaultStateCallable}
              baseMP={baseMP}
              totalMP={totalMP}
              maxStamina={maxStamina}
              hasMasterKeycard={uniqueMasterKeycards.size > 0}
            />
            <BadgeClaim 
              session={session} 
              nfts={nfts} 
              refreshNfts={() => { if (session) { fetchNfts(session.actor.toString()); fetchOnChainStakes(session.actor.toString()); } }} 
            />
            <div style={{ border: '1px solid #007bff', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
              <h4 style={{ marginTop: 0 }}>Your Stats (Level: {playerData.level})</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', alignItems: 'center' }}>
                <div><b>XP:</b> {playerData.xp} / {calculateXpForNextLevel(playerData.level)}</div>
                <div style={{ backgroundColor: '#555', borderRadius: '5px', overflow: 'hidden' }}><div style={{ width: `${xpPercentage}%`, backgroundColor: '#2196f3', padding: '2px 0' }}></div></div>
                <div><b>Daily Points:</b> {playerData.dailyPoints || 0}</div>
                <div><b>Seasonal Points:</b> {(playerData.leaderboardPoints || 0).toFixed(2)}</div>
                <div><b>⛏️ MP:</b> {totalMP}</div>
                <div><b>🛡️ Speed (Shield):</b> +{effectiveBoosts['Speed Boost'] || 0}%</div>
                <div><b>⚡ STAM:</b> {currentStaminaVal} / {maxStamina}</div>
              </div>
            </div>

            <div style={{ border: '1px solid #ccc', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>

              {gearSlots.map(slot => {
                const currentEquipped = equippedGear[slot];
                const isEquippedInGearNfts = currentEquipped ? (gearNfts[slot] || []).some(nft => nft.asset_id === currentEquipped.asset_id) : true;

                return (
                  <div key={slot}>
                    <label><b>{slot === 'Crest' ? 'Points Boost (Crest)' : slot === 'Book' ? 'XP Boost (Book)' : slot === 'Charm' ? 'NFT Chance (Charm)' : slot === 'Boots' ? 'Stamina Boost (Boots)' : slot === 'Shield' ? 'Speed Boost (Shield)' : slot === 'Sword' ? 'MP Boost (Sword)' : slot}:</b></label>
                    <select className="modern-input" name={slot} value={equippedGear[slot]?.asset_id || ''} onChange={(e) => handleSelectChange(e, 'gear')} disabled={playerData?.isBlacklisted}>
                      <option value="" style={{ color: '#888' }}>-- Unequip --</option>
                      {currentEquipped && !isEquippedInGearNfts && (
                        <option value={currentEquipped.asset_id} style={{ color: '#ffeb3b', backgroundColor: '#333' }}>
                          [Equipped] {currentEquipped.name || (currentEquipped.immutable_data && currentEquipped.immutable_data.name) || (itemTemplates && itemTemplates[currentEquipped.template?.template_id]?.name) || `Item ID: ${currentEquipped.asset_id}`}
                        </option>
                      )}
                      {(gearNfts[slot] || []).map(nft => {
                        const rarity = (itemTemplates && itemTemplates[nft.template.template_id]?.rarity) || 'Common';
                        return <option key={nft.asset_id} value={nft.asset_id} style={{ color: rarityColors[rarity] || 'white', backgroundColor: '#222' }}>{nft.name}</option>
                      })}
                    </select>
                  </div>
                )
              })}
            </div>

            {!isNative && (
              <div style={{ border: '1px solid #4caf50', padding: '15px', borderRadius: '8px' }}>
                <h4 style={{ marginTop: 0 }}>Boost MP with FIREMAN</h4>
                <p style={{ fontSize: '12px', color: '#ccc' }}>Spend 100,000 FIREMAN to gain a +10% MP Boost for 24 hours!</p>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button onClick={handleMpBoost} disabled={isBoostingMp} className="modern-button" style={{ flex: 1, backgroundColor: 'darkgreen', margin: 0 }}>
                    {isBoostingMp ? 'Activating...' : 'Buy 24hr Boost (100k FIREMAN)'}
                  </button>
                </div>
                {boostMpFeedback && <p>{boostMpFeedback}</p>}
              </div>
            )}

            <DailyBoosts
              playerData={playerData}
              nfts={nfts}
              tokenBalances={tokenBalances}

              session={session}
              fetchTokenBalances={() => {
                fetchTokenBalances(session);
                fetchNfts(session.actor.toString()); // <-- FIXED: Uses the correct function name
              }}
              MASTER_ACCOUNT_WAX_ID={MASTER_ACCOUNT_WAX_ID}
            />

          </div>
        );

      case 'game':
        return (
          <>
            <SEO title="Dungeon Runs" description="Run dungeons for rewards." path="game" />

            <DungeonTimer
              session={session}
              playerData={playerData}
              totalMP={totalMP || 0}
              totalBoost={effectiveBoosts['MP Boost'] || 0}
              speedBoost={effectiveBoosts['Speed Boost'] || 0}
              xpBoost={effectiveBoosts['XP Gain'] || 0}
              tokenBoost={effectiveBoosts['Points Gain'] || 0}
              hasUnclaimedChest={playerData?.hasUnclaimedChest || false}
              claimDailyChestCallable={functions ? httpsCallable(functions, 'claimDailyChest') : null}
              startDungeonRunCallable={functions ? httpsCallable(functions, 'startDungeonRun') : null}
              claimDungeonRunCallable={functions ? httpsCallable(functions, 'claimDungeonRun') : null}
              previewDungeonRunCallable={functions ? httpsCallable(functions, 'previewDungeonRun') : null}
              speedUpDungeonRunCallable={speedUpDungeonRunCallable}
              restAtCampfireCallable={restAtCampfireCallable}
              blessNextDungeonRunCallable={blessNextDungeonRunCallable}
              onOpenWheel={() => setIsWheelOpen(true)}
              fetchTokenBalances={() => fetchTokenBalances(session)}
            />
            <div style={{ maxWidth: '800px', margin: '20px auto', padding: '20px', backgroundColor: 'rgba(0,0,0,0.7)', borderRadius: '15px', border: '1px solid #333' }}>
              <h3 style={{ marginTop: 0, textAlign: 'center' }}>Need a Boost?</h3>
              <DailyBoosts
                playerData={playerData}
                nfts={nfts}
                tokenBalances={tokenBalances}

                session={session}
                fetchTokenBalances={() => {
                  fetchTokenBalances(session);
                  fetchNfts(session.actor.toString()); // <-- FIXED: Uses the correct function name
                }}
                MASTER_ACCOUNT_WAX_ID={MASTER_ACCOUNT_WAX_ID}
              />
            </div>
          </>
        );

      case 'leaderboard':
        return (
          <>
            <SEO title="Leaderboard" description="Top players." path="leaderboard" />
            <LeaderboardPage 
              playerData={playerData} 
              leaderboardView={leaderboardView} 
              setLeaderboardView={setLeaderboardView} 
              handleClaimMonthlyReward={handleClaimMonthlyReward}
              seasonClaim={seasonClaim}
            />
            {!isNative && (
              <>
                <LastDailyLeaderboardPage />
                <LastSeasonalLeaderboardPage />
              </>
            )}
          </>
        );

      case 'store':
        return (
          <>
            <SEO title="Item Store" description="Buy Game Packs." path="store" />
            <StorePage session={session} purchaseStoreItemCallable={purchaseStoreItemCallable} refreshNfts={() => { fetchNfts(session.actor.toString()); fetchOnChainStakes(session.actor.toString()); }} functions={functions} />
          </>
        );

      case 'hilo':
        return (
          <>
            <SEO title="Hi-Lo Jackpot" description="Play Hi-Lo." path="hilo" />
            <HiLoJackpotPage session={session} playerData={playerData} claimFaucetCallable={claimFaucetCallable} playHiLoCallable={playHiLoCallable} playCrashCallable={playCrashCallable} cashOutCrashCallable={cashOutCrashCallable} crashStatusCallable={crashStatusCallable} requestWithdrawalCallable={requestWithdrawalCallable} verifyDepositCallable={verifyDepositCallable} fetchTokenBalances={fetchTokenBalances} />
          </>
        );

      case 'vault':
        return (
          <>
            <SEO title="WHATIF Vault" description="Stake your tokens." path="vault" />
            <VaultPage
              playerData={playerData}
              tokenBalances={tokenBalances}
              vaultStakes={vaultStakes}
              vaultTotalValueLocked={vaultTotalValueLocked}
              vaultUserBoosts={vaultUserBoosts}
              selectedVaultToken={selectedVaultToken}
              setSelectedVaultToken={setSelectedVaultToken}
              vaultStakeAmountInput={vaultStakeAmountInput}
              setVaultStakeAmountInput={setVaultStakeAmountInput}
              vaultLockPeriodInput={vaultLockPeriodInput}
              setVaultLockPeriodInput={setVaultLockPeriodInput}
              vaultStakingFeedback={vaultStakingFeedback}
              setVaultStakingFeedback={setVaultStakingFeedback}
              isInitiatingVaultStake={isInitiatingVaultStake}
              handleInitiateVaultStake={handleInitiateVaultStake}
              handleClaimVaultRewards={handleClaimVaultRewards}
              handleUnstakeVaultTokens={handleUnstakeVaultTokens}
              STAKING_TIERS={STAKING_TIERS}
              VAULT_TIERS={VAULT_TIERS}
              VAULT_TOKEN_INFO={VAULT_TOKEN_INFO}
              MASTER_ACCOUNT_WAX_ID={MASTER_ACCOUNT_WAX_ID}
              handleBurnAprEmblem={handleBurnAprEmblem}

              handleClaimAllVaultRewards={handleClaimAllVaultRewards}
              handleUnstakeAll={handleUnstakeAll}
            />
          </>
        );

      case 'staking':
        return (
          <>
            <SEO title="NFT Staking" description="Stake your Gear NFTs." path="staking" />
            <NFTStakingPage
              session={session}
              playerData={playerData}
              nfts={nfts}
              stakedAttackers={stakedAttackers}
              totalMP={totalMP}
              totalBoost={effectiveBoosts['MP Boost'] || 0}
              currentStamina={currentStaminaVal || 0}
              refreshNfts={() => { fetchNfts(session.actor.toString()); fetchOnChainStakes(session.actor.toString()); }}
              initiateNftStakeCallable={initiateNftStakeCallable}
              verifyNftStakeCallable={verifyNftStakeCallable}
              unstakeNftCallable={unstakeNftCallable}
            />
          </>
        );

      case 'gangs':
        return (
          <>
            <SEO title="Gangs" description="Create or join a Gang." path="gangs" />
            <GangsPage
              session={session}
              playerData={playerData}
              createGangCallable={createGangCallable}
              joinGangCallable={joinGangCallable}
              leaveGangCallable={leaveGangCallable}
              upgradeGangBuildingCallable={upgradeGangBuildingCallable}
              depositToGangTreasuryCallable={depositToGangTreasuryCallable}
              VAULT_TOKEN_VALUE={VAULT_TOKEN_VALUE}
              activateGangPassCallable={activateGangPassCallable} // <--- ADD THIS
              gangPassNfts={gangPassNfts}                         // <--- ADD THIS
            />
          </>
        );

      case 'blending':
        return (
          <>
            <SEO title="Forge" description="Craft new items." path="blending" />
            <BlendingPage session={session} nfts={nfts} playerData={playerData} tokenBalances={tokenBalances} areBalancesLoaded={areBalancesLoaded} refreshNfts={() => { fetchNfts(session.actor.toString()); fetchOnChainStakes(session.actor.toString()); }} MASTER_ACCOUNT_WAX_ID={MASTER_ACCOUNT_WAX_ID} tokenContracts={tokenContracts} tokenDecimals={tokenDecimals} ipfsGateway={ipfsGateway} placeholderImage={placeholderImage} functions={functions} />
          </>
        );

      case 'about':
        return <><SEO title="Game Guide" description="Guide." path="about" /><AboutUs /></>;

      case 'terms':
        return <><SEO title="Terms of Service" description="Terms of Service." path="terms" /><TermsPage /></>;

      case 'privacy':
        return <><SEO title="Privacy Policy" description="Privacy Policy." path="privacy" /><PrivacyPage /></>;

      case 'partner':
        return isPartner ? (
          <>
            <SEO title="Partner Portal" description="Manage token airdrops." path="partner" />
            <PartnerPortal session={session} initiatePartnerPoolCallable={initiatePartnerPoolCallable} verifyPartnerDepositCallable={verifyPartnerDepositCallable} />
          </>
        ) : <p>Access Denied</p>;

      case 'admin':
        return isMaster ? (
          <>
            <SEO title="Admin Panel" description="Admin." path="admin" />
            <AdminPanel
              adminTargetPlayer={adminTargetPlayer} setAdminTargetPlayer={setAdminTargetPlayer}
              adminTargetPlayerStatus={adminTargetPlayerStatus}
              sendNftTarget={sendNftTarget} setSendNftTarget={setSendNftTarget}
              sendAssetId={sendAssetId} setSendAssetId={setSendAssetId}
              sendNftMemo={sendNftMemo} setSendNftMemo={setSendNftMemo}
              sendNftFeedback={sendNftFeedback}
              isSendingNft={isSendingNft}
              handleManualSendNft={handleManualSendNft}
              sendTokenTarget={sendTokenTarget} setSendTokenTarget={setSendTokenTarget}
              sendTokenAmount={sendTokenAmount} setSendTokenAmount={setSendTokenAmount}
              sendTokenSymbol={sendTokenSymbol} setSendTokenSymbol={setSendTokenSymbol}
              sendTokenMemo={sendTokenMemo} setSendTokenMemo={setSendTokenMemo}
              sendTokenFeedback={sendTokenFeedback}
              isSendingToken={isSendingToken}
              handleManualSendToken={handleManualSendToken}
              masterAccountWaxId={MASTER_ACCOUNT_WAX_ID}
              adminLoadingStatus={adminLoadingStatus}
              checkPlayerStatus={checkPlayerStatus}
              handleBlacklist={handleBlacklist}
              handleUnblacklist={handleUnblacklist}
              addPartnerCallable={addPartnerCallable}
              updatePartnerStatusCallable={updatePartnerStatusCallable}
              approvePartnerPoolCallable={approvePartnerPoolCallable}
              configureStakeableNftCallable={configureStakeableNftCallable}
              isResettingStamina={isResettingStamina}
              resetStaminaFeedback={resetStaminaFeedback}
              airdropDate={airdropDate} setAirdropDate={setAirdropDate}
              airdropAssetIds={airdropAssetIds} setAirdropAssetIds={setAirdropAssetIds}
              airdropFeedback={airdropFeedback}
              isAirdropping={isAirdropping}
              handleAirdropFromPast={handleAirdropFromPast}
              adminResetWeeklyPointsCallable={adminResetWeeklyPointsCallable}
              adminRestoreWeeklyPointsCallable={adminRestoreWeeklyPointsCallable}
              adminResetAllWeeklyPointsCallable={adminResetAllWeeklyPointsCallable}
              isResettingDailyPoints={isResettingDailyPoints}
              resetDailyPointsFeedback={resetDailyPointsFeedback}
              handleResetAllDailyPoints={handleResetAllDailyPoints}
              adminListStoreItemCallable={adminListStoreItemCallable}
              adminDelistStoreItemCallable={adminDelistStoreItemCallable}
              adminConfigureUnboxCallable={adminConfigureUnboxCallable}
              adminTroubleshootUnboxCallable={adminTroubleshootUnboxCallable}
              manualPayoutSeasonId={manualPayoutSeasonId}
              setManualPayoutSeasonId={setManualPayoutSeasonId}
              manualPayoutFeedback={manualPayoutFeedback}
              setManualPayoutFeedback={setManualPayoutFeedback}
              isManualPayoutProcessing={isManualPayoutProcessing}
              setIsManualPayoutProcessing={setIsManualPayoutProcessing}
              adminSendWeeklyPayoutCallable={adminSendWeeklyPayoutCallable}
              adminSendWeeklyDiscordAnnouncementCallable={adminSendWeeklyDiscordAnnouncementCallable}
            />
          </>
        ) : <p>Access Denied</p>;

      default:
        return null;
    }
  };
  // ADDED the missing props here! 

  return (
    <div style={{
      minHeight: '100vh',
      padding: '20px',
      backgroundSize: 'cover',
      backgroundAttachment: 'fixed',
      color: 'white',
      fontFamily: 'Arial, sans-serif',
      position: 'relative'
    }}>
      <div style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 1000, display: 'flex', alignItems: 'center', gap: '10px' }}>
        {!isNative && (
          <button
            id="android-download-top-btn"
            onClick={() => setIsAndroidPromoOpen(true)}
            className="modern-button"
            style={{
              background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.25), rgba(76, 175, 80, 0.3))',
              border: '1px solid #00e5ff',
              color: '#fff',
              fontSize: '13px',
              padding: '7px 14px',
              borderRadius: '20px',
              cursor: 'pointer',
              boxShadow: '0 0 12px rgba(0, 229, 255, 0.45)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 'bold',
              margin: 0
            }}
            title="Download Android App & Join Closed Beta"
          >
            <span>🤖</span>
            <span>Android App (Beta)</span>
          </button>
        )}
      </div>
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000, display: 'flex', alignItems: 'center', gap: '15px' }}>
        {!session && !pendingSession ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="modern-button"
              style={{
                boxShadow: '0 0 10px #4285F4',
                border: '1px solid #4285F4',
                background: 'linear-gradient(135deg, #4285F4 0%, #1a73e8 100%)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              Google
            </button>
            <button onClick={handleLogin} disabled={loading} className="modern-button" style={{ boxShadow: '0 0 10px #ff9800', border: '1px solid #ff9800' }} >
              {loading ? "Connecting..." : "🔌 Connect Wallet"}
            </button>
            <button
              id="reviewer-login-top-btn"
              onClick={() => setShowReviewerModal(true)}
              className="modern-button"
              style={{
                background: 'rgba(0, 229, 255, 0.1)',
                border: '1px solid #00e5ff',
                color: '#00e5ff',
                fontSize: '12px',
                padding: '6px 12px',
                boxShadow: '0 0 8px rgba(0,229,255,0.4)'
              }}
              title="Sign in with Google Play Reviewer credentials"
            >
              🔑 Reviewer Login
            </button>
          </div>
        ) : pendingSession ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={() => verifyIdentity()} disabled={loading} className="modern-button" style={{ boxShadow: '0 0 10px #00e5ff', border: '1px solid #00e5ff', background: 'linear-gradient(145deg, #007bff, #0056b3)' }} >
              {loading ? "Verifying..." : "🔐 VERIFY IDENTITY"}
            </button>
            <button
              onClick={async () => {
                setPendingSession(null);
                clearGoogleSession();
                if (sessionKit) await sessionKit.logout().catch(() => {});
              }}
              className="modern-button"
              style={{
                background: 'rgba(255, 68, 68, 0.2)',
                border: '1px solid #ff4444',
                color: '#ff8888',
                padding: '6px 10px',
                fontSize: '12px'
              }}
              title="Cancel and switch login method"
            >
              ✕
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(0,0,0,0.6)', padding: '5px 15px', borderRadius: '20px', border: '1px solid #444' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 'bold', color: '#ff9800', fontSize: '14px' }}>
                {session.actor.toString()}
              </div>
              <div className="stat-value index-power-val" style={{ fontWeight: 'bold' }}>
                {indexPower.toFixed(2)} IP (Updates Daily)
              </div>
              {playerData && (
                <div style={{ fontSize: '11px', color: '#ccc' }}>
                  Lvl {playerData.level} • {playerData.dailyPoints} Pts
                </div>
              )}
            </div>
            <button onClick={handleLogout} className="modern-button" style={{ background: 'darkred', fontSize: '12px', padding: '5px 10px', margin: 0 }} title="Disconnect Wallet" >
              Logout
            </button>
          </div>
        )}
      </div>
      <div style={{ textAlign: 'center', marginBottom: '20px', marginTop: '40px' }}>
        <h1 style={{
          fontSize: '48px',
          margin: '0',
          textShadow: '2px 2px 4px #000',
          color: '#ff9800',
          letterSpacing: '2px'
        }}>
          WHATIF Dungeon For Riches
        </h1>
      </div>
      <div style={{ textAlign: 'center', padding: '5px', backgroundColor: 'rgba(0, 0, 0, 0.5)', borderRadius: '8px', marginBottom: '20px', maxWidth: '800px', margin: '0 auto 20px auto' }}>
        {!isNative && (
          <p style={{ margin: 0, fontSize: '14px' }}>
            For the best prices on game tokens, visit {' '}
            <a href="https://whatifxchange.rewardsinwhatif.app/" target="_blank" rel="noopener noreferrer" style={{ color: '#4dff4d', fontWeight: 'bold' }}>
              WHATIF Xchange
            </a>.
          </p>
        )}
        {isNative ? (
          <div style={{ marginTop: '8px' }}>
            <div style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: '8px', fontWeight: 'bold' }}>
              📱 Open Our Companion Android Apps:
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => launchCompanionApp('com.puzzlrewards.app', 'https://puzzl.rewardsinwhatif.app/')}
                style={{
                  background: 'rgba(255, 152, 0, 0.15)',
                  border: '1px solid #ff9800',
                  color: '#ff9800',
                  padding: '7px 14px',
                  borderRadius: '6px',
                  fontSize: '0.82rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                🧩 PUZZL Rewards
              </button>
              <button
                type="button"
                onClick={() => launchCompanionApp('com.syndicate21.app', 'https://syndicate-21.rewardsinwhatif.app/')}
                style={{
                  background: 'rgba(255, 69, 0, 0.15)',
                  border: '1px solid #ff4500',
                  color: '#ff4500',
                  padding: '7px 14px',
                  borderRadius: '6px',
                  fontSize: '0.82rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <img src="/s21-icon.jpeg" alt="" style={{ height: '14px', width: '14px', borderRadius: '2px' }} />
                ⚔️ Syndicate 21
              </button>
            </div>
            <div style={{ fontSize: '0.68rem', color: '#666', marginTop: '5px' }}>
              * Taps launch the app on your phone, or open Google Play to install.
            </div>
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: '14px', marginTop: '5px' }}>
            Play our other games: {' '}
            <a
              href="https://puzzl.rewardsinwhatif.app/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#ff9800', fontWeight: 'bold', textDecoration: 'none' }}
            >
              PUZZL Quest
            </a>
            {' '}
            <a
              href="https://waxstation21.rewardsinwhatif.app/info"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#00bcd4', fontWeight: 'bold', textDecoration: 'none' }}
            >
              WAX Station 21
            </a>
            {' '}
            <a
              href="https://syndicate-21.rewardsinwhatif.app/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#ff4500', fontWeight: 'bold', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
            >
              <img src="/s21-icon.jpeg" alt="" style={{ height: '16px', width: '16px', borderRadius: '3px' }} />
              Syndicate 21
              <span style={{ backgroundColor: '#ff3300', color: '#fff', fontSize: '0.6rem', fontWeight: 'bold', padding: '1px 4px', borderRadius: '3px', letterSpacing: '0.5px' }}>NEW</span>
            </a>
            {' '}
            <a
              href="https://whatifhub.rewardsinwhatif.app/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#d8a765', fontWeight: 'bold', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
            >
              WhatIf Hub Central
              <span style={{ backgroundColor: '#ff3300', color: '#fff', fontSize: '0.6rem', fontWeight: 'bold', padding: '1px 4px', borderRadius: '3px', letterSpacing: '0.5px' }}>NEW</span>
            </a>
          </p>
        )}
        {!isNative && (
          <div style={{
            marginTop: '10px',
            padding: '8px 14px',
            background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.12), rgba(76, 175, 80, 0.15))',
            border: '1px solid rgba(0, 229, 255, 0.35)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            fontSize: '13px'
          }}>
            <span style={{ color: '#fff' }}>
              🤖 <strong style={{ color: '#00e5ff' }}>WHATIF Android Closed Beta is live!</strong> Play on your phone.
            </span>
            <button
              onClick={() => setIsAndroidPromoOpen(true)}
              style={{
                background: 'linear-gradient(135deg, #00e5ff, #00c853)',
                color: '#000',
                border: 'none',
                padding: '5px 12px',
                borderRadius: '6px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '12px',
                boxShadow: '0 0 8px rgba(0, 229, 255, 0.4)'
              }}
            >
              Opt-In & Download ➜
            </button>
          </div>
        )}
      </div>
      {session && (
        <div style={{
          display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap',
          padding: '10px', backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: '8px', marginBottom: '20px'
        }}>
          {isNative && (
            <span style={{ color: '#00e5ff', fontWeight: 'bold', margin: '0 10px' }}>
              🎮 In-Game Resources:
            </span>
          )}
          {TOKENS.map(token => (
            <span key={token} style={{ margin: '0 10px' }}><b>{token}:</b> {tokenBalances[token] || `0.0000 ${token}`}</span>
          ))}
        </div>
      )}
      <nav style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '30px', flexWrap: 'wrap' }}>
        <button onClick={() => navigateTo('profile')} className="modern-button" style={currentPage === 'profile' ? { border: '1px solid white' } : {}}>Profile</button>
        <button onClick={() => navigateTo('game')} className="modern-button" style={currentPage === 'game' ? { border: '1px solid white' } : {}}>Quest</button>
        {ADS_ENABLED && (
          <button onClick={() => setIsWheelOpen(true)} className="modern-button" style={{ background: 'linear-gradient(135deg, #ffd700, #ff8f00)', color: '#000', fontWeight: 'bold', boxShadow: '0 0 10px rgba(255, 215, 0, 0.4)' }}>🎰 Wheel</button>
        )}
        <button onClick={() => navigateTo('leaderboard')} className="modern-button" style={currentPage === 'leaderboard' ? { border: '1px solid white' } : {}}>Leaderboard</button>
        <button onClick={() => navigateTo('store')} className="modern-button" style={currentPage === 'store' ? { border: '1px solid white' } : {}}>Store</button>
        <button onClick={() => navigateTo('unbox')} className="modern-button" style={currentPage === 'unbox' ? { border: '1px solid white' } : {}}>Unbox</button>
        <button onClick={() => navigateTo('blending')} className="modern-button" style={currentPage === 'blending' ? { border: '1px solid white' } : {}}>Forge</button>
        {!isNative && (
          <button onClick={() => navigateTo('hilo')} className="modern-button" style={currentPage === 'hilo' ? { border: '1px solid white' } : {}}>Hi-Lo</button>
        )}
        {!isNative && (
          <>
            <button onClick={() => navigateTo('vault')} className="modern-button" style={currentPage === 'vault' ? { border: '1px solid white' } : {}}>Vault</button>
            <button onClick={() => navigateTo('staking')} className="modern-button" style={currentPage === 'staking' ? { border: '1px solid white' } : {}}>Staking</button>
          </>
        )}
        <button onClick={() => navigateTo('gangs')} className="modern-button" style={currentPage === 'gangs' ? { border: '1px solid white' } : {}}>Gangs</button>
        <button onClick={() => navigateTo('about')} className="modern-button" style={currentPage === 'about' ? { border: '1px solid white' } : {}}>Guide</button>
        <button onClick={openWalletPage} className="modern-button">🔑 My Wallet</button>

        {!isNative && isPartner && (
          <button onClick={() => navigateTo('partner')} className="modern-button" style={{ backgroundColor: '#007bff' }}>Partner Portal</button>
        )}
        {isMaster && (
          <button onClick={() => navigateTo('admin')} className="modern-button" style={{ backgroundColor: '#b300b3' }}>Admin Panel</button>
        )}
      </nav>
      <div className="main-content-wrapper" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {renderPage()}
      </div>
      {!session && !['store', 'leaderboard', 'about', 'vault', 'gangs', 'terms', 'privacy', 'hilo'].includes(currentPage) && (
        <div style={{ textAlign: 'center', marginTop: '50px', padding: '40px', background: 'rgba(0,0,0,0.5)', borderRadius: '15px', maxWidth: '600px', margin: '50px auto' }}>
          <h2>{isNative ? 'Welcome, Adventurer.' : 'Welcome, Investor.'}</h2>
          <p>
            {isNative
              ? 'Sign in to access your hero profile, manage your gear, and begin your dungeon quest.'
              : 'Connect your wallet to access your profile, manage your NFTs, and begin your journey.'}
          </p>
          {pendingSession ? (
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => verifyIdentity(pendingSession)}
                disabled={loading}
                className="modern-button"
                style={{
                  boxShadow: '0 0 15px #00e5ff',
                  border: '1px solid #00e5ff',
                  background: 'linear-gradient(145deg, #007bff, #0056b3)',
                  padding: '12px 24px',
                  fontSize: '16px'
                }}
              >
                {loading ? "Verifying..." : `🔐 VERIFY IDENTITY (${String(pendingSession.actor)})`}
              </button>
              <button
                onClick={async () => {
                  setPendingSession(null);
                  clearGoogleSession();
                  if (sessionKit) await sessionKit.logout().catch(() => {});
                }}
                className="modern-button"
                style={{
                  boxShadow: '0 0 10px rgba(255, 68, 68, 0.4)',
                  border: '1px solid #ff4444',
                  background: 'rgba(255, 68, 68, 0.2)',
                  color: '#ff8888',
                  padding: '12px 20px',
                  fontSize: '14px'
                }}
              >
                ✕ Cancel / Switch
              </button>
            </div>
          ) : (
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={handleLogin}
                disabled={loading}
                className="modern-button"
                style={{ boxShadow: '0 0 10px #ff9800', border: '1px solid #ff9800', padding: '10px 20px', fontSize: '15px' }}
              >
                {loading ? "Connecting..." : "🔌 Connect WAX Wallet"}
              </button>
              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="modern-button"
                style={{
                  boxShadow: '0 0 10px #4285F4',
                  border: '1px solid #4285F4',
                  background: 'linear-gradient(135deg, #4285F4 0%, #1a73e8 100%)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  fontSize: '15px'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24">
                  <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                  <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                </svg>
                Google
              </button>
              <button
                id="reviewer-login-hero-btn"
                onClick={() => setShowReviewerModal(true)}
                className="modern-button"
                style={{
                  background: 'rgba(0, 229, 255, 0.1)',
                  border: '1px solid #00e5ff',
                  color: '#00e5ff',
                  boxShadow: '0 0 8px rgba(0,229,255,0.4)',
                  padding: '10px 16px',
                  fontSize: '14px'
                }}
              >
                🔑 Reviewer
              </button>
            </div>
          )}
          <div style={{ marginTop: '30px', fontSize: '14px', color: '#aaa' }}>
            <p>Supported Collections:</p>
            <p style={{ color: '#ff9800' }}>whatifreward • idaretodream</p>
          </div>
        </div>
      )}
      {session && !isNative && (
        <QuartermasterAI
          playerData={playerData}
          tokenBalances={tokenBalances}
          currentStamina={playerData?.stamina || 0}
          equippedGear={equippedGear}
          quest={quest}
          userNfts={[]}
          activeBoosts={activeBoosts}
        />
      )}

      <footer style={{ textAlign: 'center', padding: '20px', marginTop: '40px', borderTop: '1px solid #444' }}>
        <button
          onClick={() => navigateTo('terms')}
          style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', textDecoration: 'underline' }}
        >
          Terms of Service
        </button>
        <span style={{ color: '#555', margin: '0 8px' }}>·</span>
        <button
          onClick={() => navigateTo('privacy')}
          style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', textDecoration: 'underline' }}
        >
          Privacy Policy
        </button>
        <AdPrivacyChoices />
        {!isNative && (
          <>
            <span style={{ color: '#555', margin: '0 8px' }}>·</span>
            <button
              onClick={() => setIsAndroidPromoOpen(true)}
              style={{ background: 'none', border: 'none', color: '#00e5ff', cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold' }}
            >
              🤖 Android App & Beta Opt-In
            </button>
          </>
        )}
        <SocialLinks />
      </footer>
      <WheelOfRichesModal
        isOpen={isWheelOpen}
        onClose={() => setIsWheelOpen(false)}
        playerData={playerData}
        spinWheelOfRichesCallable={spinWheelOfRichesCallable}
        fetchTokenBalances={() => session && fetchTokenBalances(session)}
      />
      <AndroidPromoModal
        isOpen={isAndroidPromoOpen}
        onClose={() => setIsAndroidPromoOpen(false)}
        perkName="All Android Perks"
      />
      {showReviewerModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999,
          display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px'
        }}>
          <div style={{
            background: '#1a1f2c', border: '1px solid #444', borderRadius: '12px',
            padding: '30px', maxWidth: '440px', width: '100%', color: '#fff', textAlign: 'left',
            boxShadow: '0 8px 32px rgba(0,0,0,0.8)'
          }}>
            <h2 style={{ color: '#00e5ff', marginTop: 0, marginBottom: '6px' }}>🔑 Reviewer Sign-In</h2>
            <p style={{ color: '#aaa', fontSize: '13px', marginTop: 0, marginBottom: '20px' }}>
              Google Play Reviewers: enter your credentials below to access all restricted game features.
            </p>

            {reviewerError && (
              <div style={{ background: 'rgba(244,67,54,0.15)', border: '1px solid #f44336', color: '#ff8a80', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px' }}>
                {reviewerError}
              </div>
            )}

            <form onSubmit={handleReviewerLogin}>
              <div style={{ marginBottom: '14px' }}>
                <label htmlFor="reviewer-username-input" style={{ display: 'block', fontSize: '12px', color: '#aaa', marginBottom: '4px' }}>
                  Username / Email
                </label>
                <input
                  id="reviewer-username-input"
                  type="text"
                  value={reviewerUsername}
                  onChange={(e) => setReviewerUsername(e.target.value)}
                  style={{
                    width: '100%', padding: '10px', background: '#0e1118', border: '1px solid #444',
                    borderRadius: '6px', color: '#fff', boxSizing: 'border-box'
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="reviewer-password-input" style={{ display: 'block', fontSize: '12px', color: '#aaa', marginBottom: '4px' }}>
                  Password
                </label>
                <input
                  id="reviewer-password-input"
                  type="password"
                  value={reviewerPassword}
                  onChange={(e) => setReviewerPassword(e.target.value)}
                  style={{
                    width: '100%', padding: '10px', background: '#0e1118', border: '1px solid #444',
                    borderRadius: '6px', color: '#fff', boxSizing: 'border-box'
                  }}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowReviewerModal(false)}
                  style={{
                    padding: '10px 16px', background: 'transparent', border: '1px solid #555',
                    color: '#ccc', borderRadius: '6px', cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  id="reviewer-submit-btn"
                  type="submit"
                  disabled={reviewerLoading}
                  style={{
                    padding: '10px 20px', background: 'linear-gradient(135deg, #00c6ff, #0072ff)',
                    border: 'none', color: '#fff', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer'
                  }}
                >
                  {reviewerLoading ? 'Signing In...' : 'Sign In as Reviewer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>

  );
}

export default App;